import { useState, useEffect, useRef } from "react";
import { get, post, put } from "aws-amplify/api";
import {
  Form,
  Row,
  Col,
  Card,
  InputGroup,
  Button,
  Tabs,
  Tab,
  Modal,
  Alert,
  Nav,
  NavDropdown,
} from "react-bootstrap";
import Moment from "moment";
import { PacmanLoader } from "react-spinners";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { LinkContainer } from "react-router-bootstrap";
import RefreshButton from "components/RefreshButton";
import AddressModal from "containers/property/AddressModal";
import AddressList from "containers/property/AddressList";
import BuildingGradeModal from "containers/property/BuildingGradeModal";
import BuildingNameModal from "containers/property/BuildingNameModal";
import PropertyIntegrationsModal from "containers/property/PropertyIntegrationsModal";
import PropertyClassificationModal from "containers/property/PropertyClassificationModal";
import TitleDetailsModal from "containers/property/TitleDetailsModal";
import TitleDetailsList from "containers/property/TitleDetailsList";
import VolumeFoliosModal from "containers/property/VolumeFoliosModal";
import VolumeFoliosList from "containers/property/VolumeFoliosList";
import WithholdingsModal from "containers/property/WithholdingsModal";
import PropertyAddModal from "containers/property/PropertyAddModal";
import QueryList from "containers/property/QueryList";
import PropertySaleSheetList from "containers/property/PropertySaleSheetList";
import StrataUnitList from "containers/property/StrataUnitList";
import BuildingNameList from "containers/property/BuildingNameList";
import PropertyClassificationList from "containers/property/PropertyClassificationList";
import BuildingGradeList from "containers/property/BuildingGradeList";
import PropertyIntegrationsList from "containers/property/PropertyIntegrationsList";
import PropertyArticleList from "containers/property/PropertyArticleList";
import PropertyProposalList from "containers/property/PropertyProposalList";
import NaberList from "containers/property/NaberList";
import PropertySaleList from "containers/property/PropertySaleList";
import PropertyOwnerList from "containers/property/PropertyOwnerList";
import OwnerSearchModal from "containers/owner/OwnerSearchModal";
import { useAllLookups } from "hooks/useAllLookups";
import { _s3GetURL } from "lib/awsLib";
import "containers/common/Detail.css";
import LeaseList from "containers/property/LeaseList";
import LeasingAgentList from "containers/property/LeasingAgentList";
import LiftOperatorList from "containers/property/LiftOperatorList";
import OccupancyRateList from "containers/property/OccupancyRateList";
import "containers/common/List.css";
import "containers/common/Table.css";

const EMPTY_LOOKUP_OPTIONS = [];
const PROPERTY_PHOTO_WIDTH = 240;
const PROPERTY_PHOTO_HEIGHT = 180;
const PROPERTY_TAB_FRAGMENTS = {
  0: "property",
  1: "tenants",
  2: "for-lease",
  3: "occupancy-rates",
  4: "proposals",
  5: "nabers",
  6: "lifts",
  7: "historical-listings",
  8: "sales",
  9: "ownership",
  10: "queries",
  11: "sale-sheets",
  12: "strata-units",
  13: "articles",
  14: "history",
  15: "address-details",
};

function getPropertyTabKey(hash) {
  return Object.keys(PROPERTY_TAB_FRAGMENTS).find(
    (key) => hash === `#${PROPERTY_TAB_FRAGMENTS[key]}`
  );
}

function sortPropertyPhotos(a, b) {
  const getNumber = (photo) => {
    const key = photo?.key;
    const match = key && key.match(/\[(\d+)\]/);

    return match ? parseInt(match[1], 10) : 0;
  };

  return getNumber(a) - getNumber(b);
}

function PropertyDetail({ user, isAuthenticated, userPermissions }) {
  
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const isNewProperty = params.id === "new" || location.pathname === "/properties/new";
  const isMountedRef = useRef(false);
  const propertyLoadIdRef = useRef(0);
  const activePropertyIdRef = useRef(null);
  const propertyRouteIdRef = useRef(params.id);
  propertyRouteIdRef.current = params.id;
  const pendingCheckboxSaveRef = useRef(false);
  const { lookups } = useAllLookups(isAuthenticated);
  const lookupClassificationOptions = lookups.classificationOptions || EMPTY_LOOKUP_OPTIONS;
  const lookupBuildingGradeSourceOptions = lookups.buildingGradeSourceOptions || EMPTY_LOOKUP_OPTIONS;
  const lookupBuildingGradeOptions = lookups.buildingGradeOptions || EMPTY_LOOKUP_OPTIONS;
  const lookupLiftOperatorOptions = lookups.liftOperatorOptions || EMPTY_LOOKUP_OPTIONS;
  const lookupLeaseClassificationOptions = lookups.leaseClassificationOptions || EMPTY_LOOKUP_OPTIONS;
  const withholdingReasons = lookups.withholdingReasons || EMPTY_LOOKUP_OPTIONS;
  const articleCategories = lookups.articleCategories || EMPTY_LOOKUP_OPTIONS;
  const effectiveUserPermissions = userPermissions || {};

  const [addressSaved, setAddressSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(null);
  const [modified, setModified] = useState(false);
  const [property, setProperty] = useState(null);
  const [addressFilter, setAddressFilter] = useState(null);
  const [integrationFilter, setIntegrationFilter] = useState(null);
  const [articleFilter, setArticleFilter] = useState(null);
  const [buildingGradeFilter, setBuildingGradeFilter] = useState(null);
  const [buildingNameFilter, setBuildingNameFilter] = useState(null);
  const [strataUnitFilter, setStrataUnitFilter] = useState(null);
  const [suburbFilter, setSuburbFilter] = useState(null);
  const [leaseFilter, setLeaseFilter] = useState(null);
  const [liftOperatorFilter, setLiftOperatorFilter] = useState(null);
  const [naberFilter, setNaberFilter] = useState(null);
  const [proposalFilter, setProposalFilter] = useState(null);
  const [propertyClassificationFilter, setPropertyClassificationFilter] = useState(null);
  const [propertyOwnerFilter, setPropertyOwnerFilter] = useState(null);
  const [occupancyFilter, setOccupancyFilter] = useState(null);
  const [queryFilter, setQueryFilter] = useState(null);
  const [saleFilter, setSaleFilter] = useState(null);
  const [saleSheetFilter, setSaleSheetFilter] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [tabKey, setTabKey] = useState(0);
  const [tab, setTab] = useState("property");
  const [idList, setIdList] = useState([]);
  const [previousId, setPreviousId] = useState(null);
  const [nextId, setNextId] = useState(null);
  const [currentIdIndex, setCurrentIdIndex] = useState(null);
  const [showIndexNavigation, setShowIndexNavigation] = useState(false);
  const [agentMenuIsOpen, setAgentMenuIsOpen] = useState(false);
  const [copyPropertyOwner, setCopyPropertyOwner] = useState("");
  const [copyPropertyOwnerAndDeleteFlag, setCopyPropertyOwnerAndDeleteFlag] = useState(false);
  const [reloadPage, setReloadPage] = useState(false);
  const [nabersRatingTypeOptions, setNabersRatingTypeOptions] = useState([
    { value: "Whole building", label: "Whole building" },
    { value: "Base building", label: "Base building" },
  ]);
  const [nabersRatingValues, setNabersRatingValues] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [propertyArticles, setPropertyArticles] = useState([]);
  const [withholdings, setWithholdings] = useState([]);
  const [titleDetails, setTitleDetails] = useState([]);
  const [volumeFolios, setVolumeFolios] = useState([]);
  const [buildingGrades, setBuildingGrades] = useState([]);
  const [buildingNames, setBuildingNames] = useState([]);
  const [strataUnits, setStrataUnits] = useState([]);
  const [leases, setLeases] = useState([]);
  const [leasingAgents, setLeasingAgents] = useState([]);
  const [liftOperators, setLiftOperators] = useState([]);
  const [nabers, setNabers] = useState([]);
  const [occupancyRates, setOccupancyRates] = useState([]);
  const [propertyClassifications, setPropertyClassifications] = useState([]);
  const [propertyOwners, setPropertyOwners] = useState([]);
  const [propertyProposals, setPropertyProposals] = useState([]);
  const [queries, setQueries] = useState([]);
  const [activeQueries, setActiveQueries] = useState([]);
  const [propertySales, setPropertySales] = useState([]);
  const [propertySaleSheets, setPropertySaleSheets] = useState([]);
  const [activePropertySaleSheets, setActivePropertySaleSheets] = useState([]);
  const [streetOptions, setStreetOptions] = useState([]);
  const [floorTypeOptions, setFloorTypeOptions] = useState([
    { value: "Full", label: "Full" },
    { value: "Part", label: "Part" },
  ]);
  const [leasingAgentOptions, setLeasingAgentOptions] = useState([]);
  const [leasingAgentContactOptions, setLeasingAgentContactOptions] = useState([]);
  const [cityscopeOptions, setCityscopeOptions] = useState([]);
  const [suburbOptions, setSuburbOptions] = useState([]);
  // const [buildingNameOptions, setBuildingNameOptions] = useState([]);
  const [agentOptions, setAgentOptions] = useState([]);
  const [agentContactOptions, setAgentContactOptions] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedCityscope, setSelectedCityscope] = useState(null);
  const [selectedSuburb, setSelectedSuburb] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [managing_agent_email, setManagingAgentEmail] = useState(null);
  const [selectedAgentContact, setSelectedAgentContact] = useState(null);
  const [agentSearchString, setAgentSearchString] = useState("");
  const [agentInterstateFlag, setAgentInterstateFlag] = useState(false);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [showAddressMatchingDialog, setShowAddressMatchingDialog] = useState(false);
  const [showTitleDetailsDialog, setShowTitleDetailsDialog] = useState(false);
  const [showVolumeFoliosDialog, setShowVolumeFoliosDialog] = useState(false);
  const [showWithholdingDialog, setShowWithholdingDialog] = useState(false);
  const [showPropertyAddDialog, setShowPropertyAddDialog] = useState(false);
  const [showBuildingNameDialog, setShowBuildingNameDialog] = useState(false);
  const [showPropertyClassificationDialog, setShowPropertyClassificationDialog] = useState(false);
  const [showBuildingGradeDialog, setShowBuildingGradeDialog] = useState(false);
  const [showCityscopeReference, setShowCityscopeReference] = useState(false);
  const [showAgentDropDown, setShowAgentDropDown] = useState(false);
  const [showAgentContactDropDown, setShowAgentContactDropDown] = useState(false);
  const [showOwnerSearchDialog, setShowOwnerSearchDialog] = useState(false);
  const [orig_cityscope, setOrigCityscope] = useState(null);
  const [orig_map_number, setOrigMapNumber] = useState(null);
  const [orig_map_number_suffix, setOrigMapNumberSuffix] = useState(null);
  const [orig_property_number, setOrigPropertyNumber] = useState(null);
  const [orig_property_number_suffix, setOrigPropertyNumberSuffix] = useState(null);
  const [orig_unit_number, setOrigUnitNumber] = useState(null);
  const [orig_unit_number_suffix, setOrigUnitNumberSuffix] = useState(null);
  const [orig_unit_sort, setOrigUnitSort] = useState(null);
  const [property_key, setPropertyKey] = useState(params.id);
  const [property_id, setPropertyId] = useState(null);
  const [origin_db, setOriginDb] = useState(null);
  const [cityscope, setCityscope] = useState(null);
  const [map_number, setMapNumber] = useState(null);
  const [map_number_suffix, setMapNumberSuffix] = useState(null);
  const [property_number, setPropertyNumber] = useState(null);
  const [property_number_suffix, setPropertyNumberSuffix] = useState(null);
  const [unit_number, setUnitNumber] = useState(null);
  const disableSubDividedUnit = unit_number == null || String(unit_number).trim() === "";
  const [unit_number_suffix, setUnitNumberSuffix] = useState(null);
  const [unit_sort, setUnitSort] = useState(null);
  const [parking_yes_no, setParkingYesNo] = useState(false);
  const [suburb, setSuburb] = useState(null);
  const [state, setState] = useState(null);
  const [postcode, setPostcode] = useState(null);
  const [parking_num, setParkingNum] = useState(null);
  const [bedrooms, setBedrooms] = useState(null);
  const [typical_floor_area, setTypicalFloorArea] = useState(null);
  const [storeys, setStoreys] = useState(null);
  const [building_area, setBuildingArea] = useState(null);
  const [year_built, setYearBuilt] = useState(null);
  const [year_refurb, setYearRefurb] = useState(null);
  const [site_area, setSiteArea] = useState(null);
  const [unit_area, setUnitArea] = useState(null);
  const [entity_id_managing_agent, setEntityIdManagingAgent] = useState(null);
  const [managing_agent, setManagingAgent] = useState(null);
  const [description, setDescription] = useState(null);
  const [historical_listings, setHistoricalListings] = useState(null);
  const [sales_text, setSalesText] = useState(null);
  const [title_details, setTitle_details] = useState(null);
  const [title_details_postamble, setTitleDetailsPostamble] = useState(null);
  const [title_details_archive, setTitleDetailsArchive] = useState(null);
  const [frontages_text, setFrontagesText] = useState(null);
  const [zoning_text, setZoningText] = useState(null);
  const [valuations_text, setValuationsText] = useState(null);
  const [sales_notes, setSalesNotes] = useState(null);
  const [description_notes, setDescriptionNotes] = useState(null);
  const [owner_notes, setOwnerNotes] = useState(null);
  const [frontages_notes, setFrontagesNotes] = useState(null);
  const [occupants_notes, setOccupantsNotes] = useState(null);
  const [historical_notes, setHistoricalNotes] = useState(null);
  const [title_notes, setTitleNotes] = useState(null);
  const [valuations_notes, setValuationsNotes] = useState(null);
  const [lift_notes, setLiftNotes] = useState(null);
  const [occupants_preamble, setOccupantsPreamble] = useState(null);
  const [occupants_postamble, setOccupantsPostamble] = useState(null);
  const [owner_preamble, setOwnerPreamble] = useState(null);
  const [owner_postamble, setOwnerPostamble] = useState(null);
  const [for_sale_line, setForSaleLine] = useState(null);
  const [nla_date, setNlaDate] = useState(null);
  const [lease_term, setLeaseTerm] = useState(null);
  const [description_postamble, setDescriptionPostamble] = useState(null);
  const [sales_history_postamble, setSalesHistoryPostamble] = useState(null);
  const [entity_contact_id_managing_agent, setEntityContactIdManagingAgent] = useState(null);
  const [managing_agent_contact, setManagingAgentContact] = useState(null);
  const [internet, setInternet] = useState(null);
  const [email, setEmail] = useState(null);
  const [photo_required, setPhotoRequired] = useState(false);
  const [photo_why, setPhotoWhy] = useState(null);
  const [leasing_notes, setLeasingNotes] = useState(null);
  const [title_last_searched, setTitleLastSearched] = useState(null);
  const [unit_level_number, setUnitLevelNumber] = useState(null);
  const [sub_divided_unit, setSubDividedUnit] = useState(false);
  const [auto_doors, setAutoDoors] = useState(null);
  const [head_record_property_id, setHeadRecordPropertyId] = useState(null);
  const [created_date, setCreatedDate] = useState(null);
  const [created_by, setCreatedBy] = useState(null);
  const [last_edited_date, setLastEditedDate] = useState(null);
  const [last_edited_by, setLastEditedBy] = useState(null);
  const [archived_date, setArchivedDate] = useState(null);
  const [property_withholding_id, setPropertyWithholdingId] = useState(null);
  const [withholding_text, setWithholdingText] = useState(null);
  const [withholding_reason_id, setWithholdingReasonId] = useState(null);
  const [property_withholding_created_date, setPropertyWithholdingCreatedDate] = useState(null);
  const [property_withholding_created_by, setPropertyWithholdingCreatedBy] = useState(null);
  const [previous_property_id, setPreviousPropertyId] = useState(null);
  const [next_property_id, setNextPropertyId] = useState(null);
  const [propertyPhotos, setPropertyPhotos] = useState([]);

  const normalizeCollection = (payload) => {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (!payload || typeof payload !== "object") {
      return [];
    }

    const keysToCheck = [
      "items",
      "data",
      "results",
      "rows",
      "records",
      "leasingAgents",
      "entityLeasingAgents",
      "liftOperators",
      "entityLiftOperators",
    ];

    for (const key of keysToCheck) {
      if (Array.isArray(payload[key])) {
        return payload[key];
      }
    }

    return [];
  };


  // --- Data Fetching and Update Functions ---

  function applyNavigationState(nextIdList, shouldShowIndexNavigation = true) {
    const navigationIds = Array.isArray(nextIdList) ? nextIdList : [];
    const currentIndex = navigationIds.findIndex((id) => String(id) === String(params.id));

    if (currentIndex < 0) {
      setIdList([]);
      setPreviousId(null);
      setNextId(null);
      setCurrentIdIndex(null);
      setShowIndexNavigation(false);
      return false;
    }

    setIdList(navigationIds);
    setPreviousId(currentIndex > 0 ? navigationIds[currentIndex - 1] : null);
    setNextId(currentIndex + 1 < navigationIds.length ? navigationIds[currentIndex + 1] : null);
    setCurrentIdIndex(currentIndex);
    setShowIndexNavigation(shouldShowIndexNavigation);
    return true;
  }

  useEffect(() => {
    isMountedRef.current = true;
    const propertyLoadId = ++propertyLoadIdRef.current;
    let cancelled = false;
    document.title = String(params.id || "");

    const initialiseProperty = async () => {
      const shouldForceLoader = location?.state?.forceShowLoader === true;
      const loaderStartTime = Date.now();

      try {
        setIsLoading(true);
        activePropertyIdRef.current = null;
        setAddresses([]);
        setIntegrations([]);
        setTitleDetails([]);
        setVolumeFolios([]);
        setBuildingGrades([]);
        setBuildingNames([]);
        setStrataUnits([]);
        setLeases([]);
        setLeasingAgents([]);
        setLiftOperators([]);
        setNabers([]);
        setOccupancyRates([]);
        setPropertyClassifications([]);
        setPropertyOwners([]);
        setPropertyProposals([]);
        setQueries([]);
        setActiveQueries([]);
        setPropertySales([]);
        setPropertySaleSheets([]);
        setActivePropertySaleSheets([]);
        setPropertyArticles([]);
        setWithholdings([]);
        generateNabersValues();

        //Lookups
        getCityscopes();

        if (isNewProperty) {
          setShowCityscopeReference(true);
          setCreatedDate(new Date());
          setCreatedBy(user);

          syncroniseState();
        } else {
          const [loadedPropertyId] = await Promise.all([
            retrieveProperty(propertyLoadId),
            retrievePropertyPhotos(params.id, propertyLoadId),
          ]);

          if (cancelled || propertyLoadId !== propertyLoadIdRef.current) {
            return;
          }
          if (loadedPropertyId === null) {
            return;
          }

          //If coming from proofing the browse list will be query params not state
          if (location.search.indexOf("browselist") > 0) {
            const idList = location.search.replace("?browselist=", "").split(",");
            applyNavigationState(idList, true);
          }
          if (location.state) {
            if (location.state.hasOwnProperty("data")) {
              const idList = location.state.data;
              applyNavigationState(idList, location.state.showIndexNavigation !== false);
            }
          }
          const routeTabKey = getPropertyTabKey(location.hash);
          if (routeTabKey !== undefined) {
            setTabKey(routeTabKey);
            loadTabData(routeTabKey, loadedPropertyId);
          } else if (location.state) {
            //CHeck if tab reference has been provided
            if (location.state.hasOwnProperty("tab")) {
              setTab(location.state.tab);
              if (location.state.tab === "sales") {
                setTabKey("8");
              } else if (location.state.tab === "salesheets") {
                setTabKey("11");
              } else if (location.state.tab === "owners") {
                setTabKey("9");
              } else if (location.state.tab === "leases") {
                setTabKey("1");
                loadTabData("1", loadedPropertyId);
              } else if (location.state.tab === "property") {
                setTabKey("0");
              }
            } else {
              loadTabData(tabKey, loadedPropertyId);
            }
          } else {
            loadTabData(tabKey, loadedPropertyId);
          }

        }

        await Promise.all([getTitleDetails(params.id), getVolumeFolios(false, params.id)]);

        if (shouldForceLoader) {
          const elapsed = Date.now() - loaderStartTime;
          const minimumLoaderDuration = 250;
          if (elapsed < minimumLoaderDuration) {
            await new Promise((resolve) => setTimeout(resolve, minimumLoaderDuration - elapsed));
          }
        }
      } catch (e) {
        alert(e);
      } finally {
        if (!cancelled && isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    initialiseProperty();

    return () => {
      cancelled = true;
      isMountedRef.current = false;
    };
    // Intentionally scoped to route param changes only; this mirrors the
    // legacy componentDidMount/componentDidUpdate reload and must not re-run
    // from state updates produced inside initialiseProperty itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  useEffect(() => {
    const routeTabKey = getPropertyTabKey(location.hash);
    if (routeTabKey !== undefined) {
      setTabKey(routeTabKey);
      loadTabData(routeTabKey, activePropertyIdRef.current);
    }
  }, [location.hash]);


  useEffect(() => {
    if (isNewProperty) {
      return;
    }

    setAddressFilter({ propertyid: property_id });
    setIntegrationFilter({ propertyid: property_id });
    setArticleFilter({ propertyid: property_id, articleid: null });
    setBuildingGradeFilter({ propertyid: property_id });
    setBuildingNameFilter({ propertyid: property_id });
    setStrataUnitFilter({ propertyid: property_key });
    setSuburbFilter({ cityscope: cityscope });
    if (property_id) {
      setLeaseFilter({ propertyid: property_id, archived: "" });
    }
    setLiftOperatorFilter({ propertyid: property_id });
    setNaberFilter({ propertyid: property_id });
    setProposalFilter({ propertyid: property_id });
    setPropertyClassificationFilter({ propertyid: property_id });
    setPropertyOwnerFilter({ propertyid: property_id });
    setQueryFilter({ propertyid: property_id });
    setSaleFilter({ propertyid: property_id });
    setSaleSheetFilter({ propertyid: property_id });
    setOccupancyFilter({ propertyid: property_id });
  }, [params.id, property_id, property_key, cityscope, isNewProperty]);

  useEffect(() => {
    if (!suburbFilter?.cityscope) {
      return;
    }

    getSuburbs(suburbFilter);
    getStreets(suburbFilter);
  }, [suburbFilter]);

  useEffect(() => {
    if (!pendingCheckboxSaveRef.current) {
      return;
    }

    pendingCheckboxSaveRef.current = false;
    handleBlur();
  }, [parking_yes_no, sub_divided_unit, photo_required, agentInterstateFlag, copyPropertyOwnerAndDeleteFlag, handleBlur]);

  useEffect(() => {
    const nextActiveQueries = (queries || []).filter((query) => query.active === true);
    setActiveQueries(nextActiveQueries);
  }, [queries]);

  useEffect(() => {
    const nextActivePropertySaleSheets = (propertySaleSheets || []).filter(
      (propertySaleSheet) => propertySaleSheet.current === true
    );
    setActivePropertySaleSheets(nextActivePropertySaleSheets);
  }, [propertySaleSheets]);

  function generateNabersValues() {
    const values = [];

    for (let i = 0; i <= 6; i += 0.5) {
      const rounded = Math.round(i * 10) / 10;
      values.push({ label: rounded, value: rounded });
    }

    setNabersRatingValues(values);
  }
  async function getCityscopes() {
    try {
      const { body } = await get({
        apiName: "colin",
        path: "/cityscopes/dropdown",
      }).response;
      const payload = await body.json();
      if (Array.isArray(payload)) {
        setCityscopeOptions(payload);
      }
    } catch {
      // non-fatal: leave options empty if the endpoint is unavailable
    }
  }
  function syncroniseState() {
    const propertySnapshot = {
      property_key,
      property_id,
      origin_db,
      cityscope,
      map_number,
      map_number_suffix,
      property_number,
      property_number_suffix,
      unit_number,
      unit_number_suffix,
      unit_sort,
      parking_yes_no,
      suburb,
      state,
      postcode,
      parking_num,
      bedrooms,
      typical_floor_area,
      storeys,
      building_area,
      year_built,
      year_refurb,
      site_area,
      unit_area,
      entity_id_managing_agent,
      managing_agent,
      managing_agent_email,
      description,
      historical_listings,
      sales_text,
      title_details,
      title_details_postamble,
      title_details_archive,
      frontages_text,
      zoning_text,
      valuations_text,
      sales_notes,
      description_notes,
      owner_notes,
      frontages_notes,
      occupants_notes,
      historical_notes,
      title_notes,
      valuations_notes,
      lift_notes,
      occupants_preamble,
      occupants_postamble,
      owner_preamble,
      owner_postamble,
      for_sale_line,
      nla_date,
      lease_term,
      description_postamble,
      sales_history_postamble,
      entity_contact_id_managing_agent,
      internet,
      email,
      photo_required,
      photo_why,
      leasing_notes,
      title_last_searched,
      unit_level_number,
      sub_divided_unit,
      auto_doors,
      head_record_property_id,
      created_date,
      created_by,
      last_edited_date,
      last_edited_by,
      archived_date,
      property_withholding_id,
      withholding_text,
      withholding_reason_id,
      property_withholding_created_date,
      property_withholding_created_by,
      previous_property_id,
      next_property_id,
    };

    setProperty(propertySnapshot);
    return propertySnapshot;
  }
  async function retrievePropertyPhotos(requestedPropertyKey = params.id, propertyLoadId = propertyLoadIdRef.current) {
    try {
      const basePropertyKey = requestedPropertyKey.split(".").slice(0, 2).join(".");
      const { body: baseBody } = await get({
        apiName: "colin",
        path: `/property_photos`,
        options: {
          queryParams: { propertykey: basePropertyKey },
        },
      }).response;
      const baseResult = await baseBody.json();
      const basePhotos = Array.isArray(baseResult) ? baseResult : baseResult.data || [];

      let strataUnitPhotos = [];
      if (basePropertyKey !== requestedPropertyKey) {
        const { body: strataBody } = await get({
          apiName: "colin",
          path: `/property_photos`,
          options: {
            queryParams: { propertykey: requestedPropertyKey },
          },
        }).response;
        const strataResult = await strataBody.json();
        strataUnitPhotos = Array.isArray(strataResult) ? strataResult : strataResult.data || [];
      }

      let photos = [...basePhotos, ...strataUnitPhotos]
        .filter((photo, index, allPhotos) =>
          allPhotos.findIndex((candidate) => candidate.file_path === photo.file_path) === index
        )
        .filter((photo) => photo.file_path && photo.file_path.includes("/PropsHTML/"));

      if (photos.length > 0) {
        photos = await Promise.all(
          photos.map(async (photo) => {
            try {
              let fileKey = null;
              let key = null;
              
              if (photo.file_path) {
                fileKey = photo.file_path;
                key = photo.file_path.split("/").pop()?.split(".")[0] || null;
              } else if (photo.file_name && photo.file_type) {
                fileKey = `${photo.file_name}.${photo.file_type}`;
                key = photo.file_name;
              }
              
              if (fileKey) {
                const url = await _s3GetURL(fileKey);
                return { ...photo, key, url };
              }
              return photo;
            } catch (err) {
              return photo;
            }
          })
        );

        photos.sort(sortPropertyPhotos);
      }

      if (propertyLoadId === propertyLoadIdRef.current) {
        setPropertyPhotos(photos);
      }
    } catch (error) {
      if (propertyLoadId === propertyLoadIdRef.current) {
        setPropertyPhotos([]);
      }
    }
  }
  async function getSuburbs(filter) {
    try {
      const { body } = await get({
        apiName: "colin",
        path: "/suburbs",
        options: {
          queryParams: filter || {},
        },
      }).response;

      const payload = await body.json();
      const localOptions = Array.isArray(payload)
        ? payload.map((suburbOption) => ({
            ...suburbOption,
            label:
              suburbOption.label ?? suburbOption.value ??
              (suburbOption.suburb && suburbOption.state
                ? `${suburbOption.suburb} (${suburbOption.state})`
                : suburbOption.suburb ?? ""),
            value:
              suburbOption.value ??
              (suburbOption.suburb && suburbOption.state
                ? `${suburbOption.suburb} (${suburbOption.state})`
                : suburbOption.suburb ?? ""),
          }))
        : [];

      setSuburbOptions(localOptions);
      return localOptions;
    } catch {
      alert("Error getting suburbs. Please refresh the page (F5) to try again");
      return [];
    }
  }
  async function getStreets(filter) {
    try {
      const { body } = await get({
        apiName: "colin",
        path: "/streets",
        options: {
          queryParams: filter || {},
        },
      }).response;

      const payload = await body.json();
      const localOptions = Array.isArray(payload)
        ? payload.map((streetOption) => ({
            value: streetOption.value ?? streetOption.street_name ?? streetOption.label ?? streetOption,
            label: streetOption.label ?? streetOption.street_name ?? streetOption.value ?? streetOption,
          }))
        : [];

      setStreetOptions(localOptions);
      return localOptions;
    } catch {
      alert("Error getting streets. Please refresh the page (F5) to try again");
      return [];
    }
  }
  async function getAgents(filter) {
    try {
      const queryParams = Object.fromEntries(
        Object.entries(filter || {}).filter(([, value]) => value !== "" && value !== null && value !== undefined)
      );
      const { body } = await get({
        apiName: "colin",
        path: "/entities/dropdown",
        options: {
          queryParams,
        },
      }).response;

      const payload = await body.json();
      const localOptions = Array.isArray(payload) ? payload : [];
      setAgentOptions(localOptions);
      return localOptions;
    } catch {
      alert("Error getting agents. Please refresh the page (F5) to try again");
      return [];
    }
  }

  async function getAgentContacts(filter) {
    try {
      const queryParams = Object.fromEntries(
        Object.entries(filter || {}).filter(([, value]) => value !== "" && value !== null && value !== undefined)
      );
      const { body } = await get({
        apiName: "colin",
        path: "/entitycontacts/dropdown",
        options: {
          queryParams,
        },
      }).response;

      const payload = await body.json();
      const localOptions = Array.isArray(payload) ? payload : [];
      setAgentContactOptions(localOptions);
      return localOptions;
    } catch {
      alert("Error getting agent contacts. Please refresh the page (F5) to try again");
      return [];
    }
  }

  async function getAgentsLeasing(filter) {
    try {
      const queryParams = Object.fromEntries(
        Object.entries(filter || {}).filter(([, value]) => value !== "" && value !== null && value !== undefined)
      );
      const { body } = await get({
        apiName: "colin",
        path: "/entities/dropdown",
        options: {
          queryParams,
        },
      }).response;

      const options = normalizeCollection(await body.json());
      setLeasingAgentOptions(options);
      return options;
    } catch {
      alert("Error getting leasing agents. Please refresh the page (F5) to try again");
      setLeasingAgentOptions([]);
      return [];
    }
  }

  async function getLeasingAgentContacts(filter) {
    try {
      const queryParams = Object.fromEntries(
        Object.entries(filter || {}).filter(([, value]) => value !== "" && value !== null && value !== undefined)
      );
      const { body } = await get({
        apiName: "colin",
        path: "/entitycontacts/dropdown",
        options: {
          queryParams,
        },
      }).response;

      const options = normalizeCollection(await body.json());
      setLeasingAgentContactOptions(options);
      return options;
    } catch {
      alert("Error getting leasing agent contacts. Please refresh the page (F5) to try again");
      setLeasingAgentContactOptions([]);
      return [];
    }
  }

  async function getPropertyLeasingAgents(filter) {
    const propertyId = filter?.propertyid;
    if (!propertyId) {
      return [];
    }

    try {
      const { body } = await get({
        apiName: "colin",
        path: `/properties/${propertyId}/leasingagents`,
        options: {
          queryParams: {
            propertyid: propertyId,
          },
        },
      }).response;

      const rows = normalizeCollection(await body.json());
      rows.sort((a, b) => (a.property_key > b.property_key ? 1 : b.property_key > a.property_key ? -1 : 0));
      return rows;
    } catch {
      return [];
    }
  }

  async function getPropertyLiftOperatorRows(filter) {
    const propertyId = filter?.propertyid;
    if (!propertyId) {
      return [];
    }

    try {
      const { body } = await get({
        apiName: "colin",
        path: "/liftoperators",
        options: {
          queryParams: {
            propertyid: propertyId,
          },
        },
      }).response;

      const rows = normalizeCollection(await body.json());
      rows.sort((a, b) => (a.property_key > b.property_key ? 1 : b.property_key > a.property_key ? -1 : 0));
      return rows;
    } catch {
      return [];
    }
  }

  async function getTitleDetails(requestedPropertyKey = params.id) {
    try {
      const { body } = await get({
        apiName: "colin",
        path: `/properties/${requestedPropertyKey}/parcels`,
      }).response;
      const result = await body.json();
      const parcels = Array.isArray(result) ? result : result.data || [];
      if (String(requestedPropertyKey) === String(propertyRouteIdRef.current)) {
        setTitleDetails(parcels);
      }
      return parcels;
    } catch (error) {
      console.error("Error fetching title details:", error);
      if (String(requestedPropertyKey) === String(propertyRouteIdRef.current)) {
        setTitleDetails([]);
      }
      return [];
    }
  }
  async function getVolumeFolios(fromModal = false, requestedPropertyKey = params.id) {
    try {
      const { body } = await get({
        apiName: "colin",
        path: `/properties/${requestedPropertyKey}/volume_folios`,
      }).response;
      const result = await body.json();
      const volumeFolios = Array.isArray(result) ? result : result.data || [];
      if (!fromModal && String(requestedPropertyKey) === String(propertyRouteIdRef.current)) {
        setVolumeFolios(volumeFolios);
      }
      return volumeFolios;
    } catch (error) {
      console.error("Error fetching volume folios:", error);
      if (!fromModal && String(requestedPropertyKey) === String(propertyRouteIdRef.current)) {
        setVolumeFolios([]);
      }
      return [];
    }
  }

  async function getOccupancyRates(filter) {
    const requestedPropertyId = filter?.propertyid || property_id;
    if (!requestedPropertyId) {
      return [];
    }

    try {
      const { body } = await get({
        apiName: "colin",
        path: `/properties/${requestedPropertyId}/occupancyrates`,
        options: {
          queryParams: filter || {},
        },
      }).response;

      const payload = await body.json();
      const rows = Array.isArray(payload) ? payload : payload?.data || [];
      if (String(requestedPropertyId) === String(activePropertyIdRef.current)) {
        setOccupancyRates(rows);
      }
      return rows;
    } catch (error) {
      if (String(requestedPropertyId) === String(activePropertyIdRef.current)) {
        alert("Error getting occupancy rates. Please refresh the page (F5) to try again");
        setOccupancyRates([]);
      }
      return [];
    }
  }

  async function getPropertyProposals(filter) {
    const requestedPropertyId = filter?.propertyid || property_id;
    if (!requestedPropertyId) {
      return [];
    }

    try {
      const { body } = await get({
        apiName: "colin",
        path: "/propertyproposals",
        options: {
          queryParams: { ...(filter || {}), propertyid: requestedPropertyId },
        },
      }).response;

      const payload = await body.json();
      const rows = Array.isArray(payload) ? payload : payload?.data || [];
      if (String(requestedPropertyId) === String(activePropertyIdRef.current)) {
        setPropertyProposals(rows);
      }
      return rows;
    } catch {
      if (String(requestedPropertyId) === String(activePropertyIdRef.current)) {
        alert("Error getting proposals. Please refresh the page (F5) to try again");
        setPropertyProposals([]);
      }
      return [];
    }
  }

  async function getNabers(filter) {
    const requestedPropertyId = filter?.propertyid || property_id;
    if (!requestedPropertyId) {
      return [];
    }

    try {
      const { body } = await get({
        apiName: "colin",
        path: `/properties/${requestedPropertyId}/nabers`,
        options: {
          queryParams: filter || {},
        },
      }).response;

      const payload = await body.json();
      const rows = Array.isArray(payload) ? payload : payload?.data || [];
      if (String(requestedPropertyId) === String(activePropertyIdRef.current)) {
        setNabers(rows);
      }
      return rows;
    } catch {
      if (String(requestedPropertyId) === String(activePropertyIdRef.current)) {
        alert("Error getting nabers. Please refresh the page (F5) to try again");
        setNabers([]);
      }
      return [];
    }
  }

  async function getLeases(filter) {
    const requestedPropertyId = filter?.propertyid || property_id;
    if (!requestedPropertyId) {
      return [];
    }

    try {
      const { body } = await get({
        apiName: "colin",
        path: `/properties/${requestedPropertyId}/leases`,
        options: {
          queryParams: filter || {},
        },
      }).response;

      const payload = await body.json();
      const rows = Array.isArray(payload) ? payload : payload?.data || [];
      if (String(requestedPropertyId) === String(activePropertyIdRef.current)) {
        setLeases(rows);
      }
      return rows;
    } catch {
      if (String(requestedPropertyId) === String(activePropertyIdRef.current)) {
        alert("Error getting leases. Please refresh the page (F5) to try again");
        setLeases([]);
      }
      return [];
    }
  }

  async function getPropertyArticles(propertyId) {
    const { body } = await get({
      apiName: "colin",
      path: "/propertyarticles",
      options: {
        queryParams: { propertyid: propertyId, articleid: -1 },
      },
    }).response;

    const payload = await body.json();
    return Array.isArray(payload) ? payload : payload?.data || [];
  }

  async function retrieveProperty(propertyLoadId) {
    const localProperty = await getProperty({ user: user });

    if (isMountedRef.current && propertyLoadId === propertyLoadIdRef.current) {
      if (Object.entries(localProperty).length > 0) {
        const {
          property_key : local_property_key,
          property_id : local_property_id,
          origin_db : local_origin_db,
          cityscope : local_cityscope,
          map_number : local_map_number,
          map_number_suffix : local_map_number_suffix,
          property_number : local_property_number,
          property_number_suffix : local_property_number_suffix,
          unit_number : local_unit_number,
          unit_number_suffix : local_unit_number_suffix,
          unit_sort : local_unit_sort,
          parking_yes_no : local_parking_yes_no,
          suburb : local_suburb,
          state : local_state,
          postcode : local_postcode,
          parking_num : local_parking_num,
          bedrooms : local_bedrooms,
          typical_floor_area : local_typical_floor_area,
          storeys : local_storeys,
          building_area : local_building_area,
          year_built : local_year_built,
          year_refurb : local_year_refurb,
          site_area : local_site_area,
          unit_area : local_unit_area,
          entity_id_managing_agent : local_entity_id_managing_agent,
          managing_agent : local_managing_agent,
          managing_agent_email : local_managing_agent_email,
          description : local_description,
          historical_listings : local_historical_listings,
          sales_text : local_sales_text,
          title_details : local_title_details,
          title_details_postamble : local_title_details_postamble,
          title_details_archive : local_title_details_archive,
          frontages_text : local_frontages_text,
          zoning_text : local_zoning_text,
          valuations_text : local_valuations_text,
          sales_notes : local_sales_notes,
          description_notes : local_description_notes,
          owner_notes : local_owner_notes,
          frontages_notes : local_frontages_notes,
          occupants_notes : local_occupants_notes,
          historical_notes : local_historical_notes,
          title_notes : local_title_notes,
          valuations_notes : local_valuations_notes,
          lift_notes : local_lift_notes,
          occupants_preamble : local_occupants_preamble,
          occupants_postamble : local_occupants_postamble,
          owner_preamble : local_owner_preamble,
          owner_postamble : local_owner_postamble,
          for_sale_line : local_for_sale_line,
          nla_date : local_nla_date,
          lease_term : local_lease_term,
          description_postamble : local_description_postamble,
          sales_history_postamble : local_sales_history_postamble,
          entity_contact_id_managing_agent : local_entity_contact_id_managing_agent,
          managing_agent_contact : local_managing_agent_contact,
          internet : local_internet,
          email : local_email,
          photo_required : local_photo_required,
          photo_why : local_photo_why,
          leasing_notes : local_leasing_notes,
          title_last_searched : local_title_last_searched,
          unit_level_number : local_unit_level_number,
          sub_divided_unit : local_sub_divided_unit,
          auto_doors : local_auto_doors,
          head_record_property_id : local_head_record_property_id,
          created_date : local_created_date,
          created_by : local_created_by,
          last_edited_date : local_last_edited_date,
          last_edited_by : local_last_edited_by,
          archived_date : local_archived_date,
          property_withholding_id : local_property_withholding_id,
          withholding_text : local_withholding_text,
          withholding_reason_id : local_withholding_reason_id,
          property_withholding_created_date : local_property_withholding_created_date,
          property_withholding_created_by : local_property_withholding_created_by,
          previous_property_id : local_previous_property_id,
          next_property_id : local_next_property_id,
          addresses : local_addresses,
          integrations : local_integrations,
          buildinggrades : local_buildinggrades,
          buildingnames : local_buildingnames,
          strataunits : local_strataunits,
          propertyclassifications : local_propertyclassifications,
          queries : local_queries,
          propertysalesheets : local_propertysalesheets,
          propertyowners : local_propertyowners,
          propertysales : local_propertysales,
          propertywithholdings : local_propertywithholdings,
        } = localProperty;

        const localPropertyArticles = await getPropertyArticles(local_property_id);
        if (!isMountedRef.current || propertyLoadId !== propertyLoadIdRef.current) {
          return;
        }

        activePropertyIdRef.current = local_property_id;
        setProperty(localProperty);
        setPropertyKey(local_property_key);
        setPropertyId(local_property_id);
        setOriginDb(local_origin_db);
        setCityscope(local_cityscope);
        setSelectedCityscope(
          local_cityscope ? { value: local_cityscope, label: local_cityscope } : { value: null, label: null }
        );
        setMapNumber(local_map_number);
        setMapNumberSuffix(local_map_number_suffix);
        setPropertyNumber(local_property_number);
        setPropertyNumberSuffix(local_property_number_suffix);
        setUnitNumber(local_unit_number);
        setUnitNumberSuffix(local_unit_number_suffix);
        setUnitSort(local_unit_sort);
        setParkingYesNo(local_parking_yes_no);
        setSuburb(local_suburb);
        setState(local_state);
        setPostcode(local_postcode);
        setSelectedSuburb(
          local_suburb != null
            ? {
                value: local_state ? `${local_suburb} (${local_state})` : `${local_suburb}`,
                label: local_state ? `${local_suburb} (${local_state})` : `${local_suburb}`,
                suburb: local_suburb,
                state: local_state,
                postcode: local_postcode,
              }
            : null
        );
        setParkingNum(local_parking_num);
        setBedrooms(local_bedrooms);
        setTypicalFloorArea(local_typical_floor_area);
        setStoreys(local_storeys);
        setBuildingArea(local_building_area);
        setYearBuilt(local_year_built);
        setYearRefurb(local_year_refurb);
        setSiteArea(local_site_area);
        setUnitArea(local_unit_area);
        setEntityIdManagingAgent(local_entity_id_managing_agent);
        setManagingAgent(local_managing_agent);
        setManagingAgentEmail(local_managing_agent_email);
        setSelectedAgent(
          local_entity_id_managing_agent
            ? { value: local_entity_id_managing_agent, label: local_managing_agent }
            : { value: null, label: null }
        );
        setDescription(local_description);
        setHistoricalListings(local_historical_listings);
        setSalesText(local_sales_text);
        setTitle_details(local_title_details);
        setTitleDetailsPostamble(local_title_details_postamble);
        setTitleDetailsArchive(local_title_details_archive);
        setFrontagesText(local_frontages_text);
        setZoningText(local_zoning_text);
        setValuationsText(local_valuations_text);
        setSalesNotes(local_sales_notes);
        setDescriptionNotes(local_description_notes);
        setOwnerNotes(local_owner_notes);
        setFrontagesNotes(local_frontages_notes);
        setOccupantsNotes(local_occupants_notes);
        setHistoricalNotes(local_historical_notes);
        setTitleNotes(local_title_notes);
        setValuationsNotes(local_valuations_notes);
        setLiftNotes(local_lift_notes);
        setOccupantsPreamble(local_occupants_preamble);
        setOccupantsPostamble(local_occupants_postamble);
        setOwnerPreamble(local_owner_preamble);
        setOwnerPostamble(local_owner_postamble);
        setForSaleLine(local_for_sale_line);
        setNlaDate(local_nla_date);
        setLeaseTerm(local_lease_term);
        setDescriptionPostamble(local_description_postamble);
        setSalesHistoryPostamble(local_sales_history_postamble);
        setEntityContactIdManagingAgent(local_entity_contact_id_managing_agent);
        setManagingAgentContact(local_managing_agent_contact);
        setSelectedAgentContact(
          local_entity_contact_id_managing_agent
            ? {
                value: local_entity_contact_id_managing_agent,
                label: local_managing_agent_contact,
              }
            : { value: null, label: null }
        );
        setInternet(local_internet);
        setEmail(local_email);
        setPhotoRequired(local_photo_required);
        setPhotoWhy(local_photo_why);
        setLeasingNotes(local_leasing_notes);
        setTitleLastSearched(local_title_last_searched);
        setUnitLevelNumber(local_unit_level_number);
        setSubDividedUnit(local_sub_divided_unit);
        setAutoDoors(local_auto_doors);
        setHeadRecordPropertyId(local_head_record_property_id);
        setCreatedDate(local_created_date);
        setCreatedBy(local_created_by);
        setLastEditedDate(local_last_edited_date);
        setLastEditedBy(local_last_edited_by);
        setArchivedDate(local_archived_date);
        setPropertyWithholdingId(local_property_withholding_id);
        setWithholdingText(local_withholding_text);
        setWithholdingReasonId(local_withholding_reason_id);
        setPropertyWithholdingCreatedDate(local_property_withholding_created_date);
        setPropertyWithholdingCreatedBy(local_property_withholding_created_by);
        setPreviousPropertyId(local_previous_property_id);
        setNextPropertyId(local_next_property_id);
        setAddresses(local_addresses || []);
        setIntegrations(local_integrations || []);
        setBuildingGrades(local_buildinggrades || []);
        setBuildingNames(local_buildingnames || []);
        setStrataUnits(local_strataunits || []);
        setPropertyClassifications(local_propertyclassifications || []);
        setQueries(local_queries || []);
        setPropertySaleSheets(local_propertysalesheets || []);
        setPropertyOwners(local_propertyowners || []);
        setPropertySales(local_propertysales || []);
        setPropertyArticles(localPropertyArticles);
        setWithholdings(local_propertywithholdings || []);

        const propertyLeasingAgents = await getPropertyLeasingAgents({ propertyid: local_property_id });
        if (!isMountedRef.current || propertyLoadId !== propertyLoadIdRef.current) {
          return;
        }
        setLeasingAgents(propertyLeasingAgents);

        const propertyLiftOperatorRecords = await getPropertyLiftOperatorRows({ propertyid: local_property_id });
        if (!isMountedRef.current || propertyLoadId !== propertyLoadIdRef.current) {
          return;
        }
        setLiftOperators(propertyLiftOperatorRecords);

        if (local_entity_id_managing_agent) {
          await getAgents({ entityid: local_entity_id_managing_agent });
          setShowAgentDropDown(true);

          await getAgentContacts({ entityid: local_entity_id_managing_agent });
          setShowAgentContactDropDown(true);
        } else {
          setShowAgentDropDown(false);
          setShowAgentContactDropDown(false);
        }

        return local_property_id;
      } else {
        alert("The cityscope reference entered does not exist. Please use Add New Property to create a new record.");
        navigate("/properties");
        return null;
      }
    }
  }

  //Call API
  async function getProperty(filter) {
    const { body } = await get({
      apiName: "colin",
      path: `/properties/${params.id}`,
      options: {
        queryParams: filter,
      },
    }).response;

    try {
      return await body.json();
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    }
  }

  async function getIntegrations() {
    const { body } = await get({
      apiName: "colin",
      path: `/properties/${params.id}/integrations`,
    }).response;

    const integrations = await body.json();
    setIntegrations(integrations);
  }

  async function saveProperty(property, force = false) {
    property.force = force;
    if (isNewProperty && property_id === null) {
      const { body } = await post({
        apiName: "colin",
        path: `/properties/new`,
        options: { body: property },
      }).response;
      try {
        return await body.json();
      } catch (error) {
        alert(error + ". Please refresh the page (F5) to try again.");
      }
    } else {
      const { body } = await put({
        apiName: "colin",
        path: `/properties/${property_id}`,
        options: {
          body: property,
        },
      }).response;
      try {
        return await body.json();
      } catch (error) {
        alert(error + ". Please refresh the page (F5) to try again.");
      }
    }
  }

  function handleCurrentIdIndexChange(event) {
    const { value } = event.target;
    if (value === "") {
      setCurrentIdIndex("");
      return;
    }

    const parsedValue = Number.parseInt(value, 10);
    if (Number.isNaN(parsedValue)) {
      return;
    }

    setCurrentIdIndex(parsedValue - 1);
  }

  function handleGoToRecord() {
    const parsedIndex = Number.parseInt(currentIdIndex, 10);
    if (Number.isNaN(parsedIndex) || parsedIndex < 0 || parsedIndex >= idList.length) {
      const actualIndex = idList.indexOf(params.id);
      setCurrentIdIndex(actualIndex >= 0 ? actualIndex : null);
      return;
    }

    const nextPropertyKey = idList[parsedIndex];
    navigate(`/properties/${nextPropertyKey}`, {
      state: {
        data: idList,
        showIndexNavigation,
      },
    });
  }

  function handleEnterCurrentIdIndex(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      handleGoToRecord();
    }
  }

  function navigateToProperty(nextPropertyKey, nextIdList = [], nextShowIndexNavigation = false) {
    navigate(`/properties/${nextPropertyKey}`, {
      state: {
        data: nextIdList,
        showIndexNavigation: nextShowIndexNavigation,
      },
    });
  }
  function toggleWithholdingModal() {
    setShowWithholdingDialog(!showWithholdingDialog);
  }

  async function refreshWithholdings() {
    try {
      const localProperty = await getProperty({ user });
      const localWithholdings =
        localProperty?.propertywithholdings || localProperty?.withholdings || [];
      setWithholdings(Array.isArray(localWithholdings) ? localWithholdings : []);
    } catch (error) {
      console.error("Error refreshing withholdings:", error);
    }
  }

  async function bulkSaveWithholdings(records) {
    if (!Array.isArray(records) || records.length === 0) {
      return [];
    }

    const saved = [];
    for (const record of records) {
      const result = await saveRecord("withholdings", record);
      saved.push(result);
    }
    return saved;
  }

  function togglePropertyAddModal() {
    if (!showPropertyAddDialog) {
      // Match UAT behavior: open with a prefilled copy of the current property.
      setProperties((prev) => {
        if (Array.isArray(prev) && prev.length > 0) {
          return prev;
        }

        const seedProperty = {
          cityscope,
          map_number,
          map_number_suffix,
          property_number,
          property_number_suffix,
          unit_number,
          unit_number_suffix,
          unit_sort,
          description,
          unit_area,
          unit_level_number,
          bedrooms,
          parking_yes_no,
          parking_num,
          title_details,
          title_details_postamble,
          title_details_archive,
          suburb,
          postcode,
          state,
          zoning_text,
          photo_required: false,
          created_by: user,
          last_edited_by: user,
          sub_divided_unit: false,
          property_id: null,
          new_record: true,
        };

        return [seedProperty];
      });
      setShowPropertyAddDialog(true);
      return;
    }

    setShowPropertyAddDialog(false);
    setProperties([]);
  }
  async function handleHold() {
    const userFlaggedReason = (withholdingReasons || []).find(
      (reason) => reason?.label === "User Flagged"
    );

    if (!userFlaggedReason) {
      alert("Unable to add HOLD: 'User Flagged' withholding reason is missing.");
      return;
    }

    const nextWithholding = {
      property_key,
      withholding_reason_id: userFlaggedReason.value,
      withholding_reason: "User Flagged",
      withholding_text: null,
      primary_classification_code:
        propertyClassifications.length > 0
          ? propertyClassifications[0].classification_code
          : null,
      primary_classification:
        propertyClassifications.length > 0
          ? propertyClassifications[0].classification
          : null,
      link_id: null,
      link: null,
      property_id,
      scoped_by: user,
      created_by: user,
      last_edited_by: user,
      new_record: true,
      modified: true,
      selected: false,
      _client_id: `withholding-${Date.now()}`,
    };

    setWithholdings((prev) => [...(prev || []), nextWithholding]);
    setShowWithholdingDialog(true);

    try {
      // Match UAT behavior where creating a HOLD issues the create request immediately.
      await saveRecord("withholdings", nextWithholding);
      await refreshWithholdings();
    } catch (error) {
      console.error("Failed to create hold record:", error);
    }
  }

  function loadTabData(nextTabKey, requestedPropertyId) {
    // Lazy-load occupancy rates only when Occ. Rates tab is clicked
    if ((nextTabKey === 3 || nextTabKey === "3") && requestedPropertyId) {
      getOccupancyRates({ ...(occupancyFilter || {}), propertyid: requestedPropertyId });
    }

    // Lazy-load Proposals when Proposals tab is clicked
    if ((nextTabKey === 4 || nextTabKey === "4") && requestedPropertyId) {
      getPropertyProposals({ ...(proposalFilter || {}), propertyid: requestedPropertyId });
    }

    // Lazy-load NABERS only when Nabers tab is clicked
    if ((nextTabKey === 5 || nextTabKey === "5") && requestedPropertyId) {
      getNabers({ ...(naberFilter || {}), propertyid: requestedPropertyId });
    }

    // Match UAT behavior: load leases only when Tenants tab is selected.
    if ((nextTabKey === 1 || nextTabKey === "1") && requestedPropertyId) {
      getLeases({ ...(leaseFilter || {}), propertyid: requestedPropertyId, archived: "" });
    }
  }

  function onTabSelect(nextTabKey) {
    if (nextTabKey === null || nextTabKey === undefined) {
      return;
    }

    setTabKey(nextTabKey);
    loadTabData(nextTabKey, property_id);
  }

  function getPropertyTabAttrs(key) {
    return {
      as: "a",
      type: undefined,
      href: `/properties/${property_key || params.id}${location.search}#${PROPERTY_TAB_FRAGMENTS[key]}`,
      onClick: (event) => {
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
          event.stopPropagation();
          return;
        }
        event.preventDefault();
      },
    };
  }

  async function toggleAddressModal() {
    const isClosingDialog = showAddressDialog;
    setShowAddressDialog((prev) => !prev);

    if (isClosingDialog && addressSaved) {
      setIsLoading(true);
      try {
        await getIntegrations();
        setShowAddressMatchingDialog(true);
      } finally {
        setIsLoading(false);
        setAddressSaved(false);
      }
    }
  }
  function handleSuburbChange(nextSelectedSuburb) {
    const normalize = (value) => (value == null ? "" : String(value).trim());
    const parseSuburbLabel = (input) => {
      const text = normalize(input);
      if (!text) {
        return { suburb: "", state: "" };
      }

      const match = text.match(/^(.*?)(?:\s*\(([^)]+)\))?$/);
      return {
        suburb: normalize(match?.[1]),
        state: normalize(match?.[2]),
      };
    };

    if (!nextSelectedSuburb) {
      const isAlreadyEmpty = !selectedSuburb && !suburb && !state && !postcode;
      if (isAlreadyEmpty) {
        return;
      }

      setSelectedSuburb(null);
      setSuburb(null);
      setState(null);
      setPostcode(null);
      setModified(true);
      return;
    }

    const matchedOption = (suburbOptions || []).find((option) => {
      return (
        normalize(option?.value) === normalize(nextSelectedSuburb?.value) ||
        normalize(option?.label) === normalize(nextSelectedSuburb?.label)
      );
    });

    const parsedFromLabel = parseSuburbLabel(nextSelectedSuburb?.label || nextSelectedSuburb?.value);
    const isNzCityscope = ["AK", "WN", "CC"].includes(cityscope);
    const nextSuburb =
      normalize(nextSelectedSuburb?.suburb) ||
      normalize(matchedOption?.suburb) ||
      parsedFromLabel.suburb ||
      null;
    const nextState = isNzCityscope
      ? "NZ"
      : normalize(nextSelectedSuburb?.state) ||
        normalize(matchedOption?.state) ||
        parsedFromLabel.state ||
        state ||
        null;
    const nextPostcode =
      normalize(nextSelectedSuburb?.postcode) || normalize(matchedOption?.postcode) || null;
    const normalizedSelection = matchedOption || nextSelectedSuburb;

    const currentSelectionKey = `${normalize(selectedSuburb?.value)}|${normalize(
      selectedSuburb?.label
    )}`;
    const nextSelectionKey = `${normalize(normalizedSelection?.value)}|${normalize(
      normalizedSelection?.label
    )}`;
    const isSameSelection = currentSelectionKey === nextSelectionKey;
    const isSameSuburbStatePostcode =
      normalize(suburb) === normalize(nextSuburb) &&
      normalize(state) === normalize(nextState) &&
      normalize(postcode) === normalize(nextPostcode);

    if (isSameSelection && isSameSuburbStatePostcode) {
      return;
    }

    setSelectedSuburb(normalizedSelection);
    setSuburb(nextSuburb);
    setState(nextState);
    setPostcode(nextPostcode);
    setModified(true);

    setTimeout(async () => {
      await handleBlur();

      for (const address of addresses) {
        try {
          await saveAddressesRecord(address, {
            suburb: nextSuburb,
            state: nextState,
            postcode: nextPostcode,
          });
        } catch (error) {
          console.error("Failed to save address after suburb update:", error);
        }
      }

      try {
        await getIntegrations();
      } catch (error) {
        console.error("Failed to refresh integrations after suburb update:", error);
      }
    }, 0);
  }
  function handleInputChange() {}



  function buildPropertyKey() {
    let nextPropertyKey = `${cityscope || ""}${(map_number || "").toString()}${
      map_number_suffix || ""
    }.${(property_number || "").toString()}${property_number_suffix || ""}`;

    if (unit_number !== null && unit_number !== "") {
      nextPropertyKey = `${nextPropertyKey}.${(unit_number || "").toString()}${
        unit_number_suffix || ""
      }`;
    }

    if (nextPropertyKey !== property_key) {
      setPropertyKey(nextPropertyKey);
      setReloadPage(true);
    }

    return nextPropertyKey;
  }

  async function handleBlur(event) {
    if (event?.persist) {
      event.persist();
    }

    if (!modified) {
      return;
    }

    let areaTransfer = {};
    const enteredArea = (value) => value !== null && value !== undefined && String(value).trim() !== "";

    if (event?.target?.id === "building_area" && !disableSubDividedUnit && enteredArea(building_area)) {
      const moveArea = window.confirm(
        "Please note: Building Area is typically not be populated for Unit records. Would you like to move the value entered into the Unit Area field?"
      );

      if (moveArea) {
        areaTransfer = { building_area: "", unit_area: building_area };
        setBuildingArea("");
        setUnitArea(building_area);
      }
    } else if (event?.target?.id === "unit_area" && disableSubDividedUnit && enteredArea(unit_area)) {
      const moveArea = window.confirm(
        "Please note: Unit Area is typically not be populated for non-unit records. Would you like to move the value entered into the Building Area field?"
      );

      if (moveArea) {
        areaTransfer = { building_area: unit_area, unit_area: "" };
        setBuildingArea(unit_area);
        setUnitArea("");
      }
    }

    try {
      const nextPropertyKey = buildPropertyKey();
      setShowAlert(false);
      setLastEditedBy(user);

      const propertySnapshot = { ...syncroniseState(), ...areaTransfer };
      propertySnapshot.property_key = nextPropertyKey;
      propertySnapshot.last_edited_by = user;

      const validation = validateRelatedRecord(propertySnapshot, "property");
      if (!validation?.[0]) {
        setErrorMessage(validation?.[1] || "Validation failed");
        setShowAlert(true);
        return;
      }

      setModified(false);
      if (isNewProperty) {
        setIsLoading(true);
      }

      let result = await saveProperty(propertySnapshot);

      if (result?.result === false) {
        if (result?.error_message) {
          alert(result.error_message);
          if (isNewProperty) {
            setIsLoading(false);
          }
          return;
        }

        const confirmed = window.confirm(
          "The record has been updated by another user. Do you want to force your changes through (this will overwrite changes from the other user)?"
        );

        if (confirmed) {
          result = await saveProperty(propertySnapshot, true);
        }
      }

      if (result?.last_edited_date) {
        setLastEditedDate(result.last_edited_date);
      }

      const savedPropertyId = result?.id ?? property_id;
      const savedPropertyKey = result?.property_key || nextPropertyKey;

      if (savedPropertyId && !property_id) {
        setPropertyId(savedPropertyId);
      }
      if (savedPropertyKey) {
        setPropertyKey(savedPropertyKey);
      }
      setShowCityscopeReference(false);

      if (isNewProperty) {
        navigate(`/properties/${savedPropertyKey}`, { replace: true });
      } else if (reloadPage) {
        navigate(`/properties/${savedPropertyKey}`, { replace: true });
      }

      if (event?.target?.id === "title_details_postamble") {
        await updateTitleDetailsString();
      }

      if (isNewProperty) {
        setIsLoading(false);
      }
    } catch (e) {
      if (isNewProperty) {
        setIsLoading(false);
      }
      alert(e);
    }
  }

  function handleChange(event) {
    const { id, value } = event.target;

    const fieldSetters = {
      copyPropertyOwner: setCopyPropertyOwner,
      cityscope: setCityscope,
      map_number: setMapNumber,
      map_number_suffix: setMapNumberSuffix,
      property_number: setPropertyNumber,
      property_number_suffix: setPropertyNumberSuffix,
      unit_number: setUnitNumber,
      unit_number_suffix: setUnitNumberSuffix,
      unit_sort: setUnitSort,
      frontages_text: setFrontagesText,
      zoning_text: setZoningText,
      description: setDescription,
      description_postamble: setDescriptionPostamble,
      description_notes: setDescriptionNotes,
      year_built: setYearBuilt,
      year_refurb: setYearRefurb,
      bedrooms: setBedrooms,
      storeys: setStoreys,
      parking_num: setParkingNum,
      typical_floor_area: setTypicalFloorArea,
      building_area: setBuildingArea,
      site_area: setSiteArea,
      unit_area: setUnitArea,
      unit_level_number: setUnitLevelNumber,
      internet: setInternet,
      email: setEmail,
      title_details_postamble: setTitleDetailsPostamble,
      title_notes: setTitleNotes,
      historical_listings: setHistoricalListings,
      historical_notes: setHistoricalNotes,
      occupants_preamble: setOccupantsPreamble,
      occupants_postamble: setOccupantsPostamble,
      occupants_notes: setOccupantsNotes,
      leasing_notes: setLeasingNotes,
      lift_notes: setLiftNotes,
      sales_text: setSalesText,
      sales_history_postamble: setSalesHistoryPostamble,
      sales_notes: setSalesNotes,
      owner_preamble: setOwnerPreamble,
      owner_postamble: setOwnerPostamble,
      lease_term: setLeaseTerm,
      owner_notes: setOwnerNotes,
      photo_why: setPhotoWhy,
      agentSearchString: setAgentSearchString,
      title_details: setTitle_details,
      title_details_archive: setTitleDetailsArchive,
      for_sale_line: setForSaleLine,
    };

    if (fieldSetters[id]) {
      if (id === "cityscope") {
        fieldSetters[id](String(value || "").toUpperCase());
      } else {
        fieldSetters[id](value);
      }
      setModified(true);
    }

  }
  function togglePropertyIntegrationsModal() {
    setShowAddressMatchingDialog((prev) => !prev);
  }
  async function updateIntegrations() {
    try {
      const address = addresses[0];
      if (!address) {
        throw new Error("Address 0 not found");
      }

      await saveAddressesRecord(address);
      await getIntegrations();
    } catch (error) {
      console.warn(`Address 0 not found in ${addresses}`, error);
    }
  }
  async function saveIntegration(integration) {
    const record = {
      ...integration,
      last_edited_date: new Date().toISOString(),
      last_edited_by: user,
    };

    const { body } = await put({
      apiName: "colin",
      path: `/properties/${property_id}/integrations/${integration.property_integration_id}`,
      options: {
        body: record,
      },
    }).response;

    try {
      await body.json();
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    }

    return record;
  }

  async function acceptIntegration(row) {
    const updatedRecord = {
      ...row.original,
      is_accepted: true,
      accept_reject_by: user,
    };

    try {
      const savedRecord = await saveIntegration(updatedRecord);
      setIntegrations((prev) =>
        prev.map((integration) =>
          integration.property_integration_id === savedRecord.property_integration_id
            ? { ...integration, ...savedRecord }
            : integration
        )
      );
    } catch (error) {
      console.error("Accept integration failed:", error);
    }
  }

  async function rejectIntegration(row) {
    const updatedRecord = {
      ...row.original,
      is_accepted: false,
      accept_reject_by: user,
    };

    try {
      const savedRecord = await saveIntegration(updatedRecord);
      setIntegrations((prev) =>
        prev.map((integration) =>
          integration.property_integration_id === savedRecord.property_integration_id
            ? { ...integration, ...savedRecord }
            : integration
        )
      );
    } catch (error) {
      console.error("Reject integration failed:", error);
    }
  }
  async function updateTitleDetailsString() {
    try {
      const response = await getProperty({ user });
      setTitle_details(response?.title_details ?? "");
    } catch (error) {
      console.error("Failed to refresh title details string:", error);
    }
  }

  async function toggleTitleDetailsModal() {
    const isClosingDialog = showTitleDetailsDialog;
    setShowTitleDetailsDialog((prev) => !prev);

    if (!isClosingDialog) {
      getTitleDetails();
      return;
    }

    await updateTitleDetailsString();
  }
  async function toggleVolumeFoliosModal() {
    const isClosingDialog = showVolumeFoliosDialog;
    setShowVolumeFoliosDialog((prev) => !prev);

    if (!isClosingDialog) {
      getVolumeFolios();
      return;
    }

    await updateTitleDetailsString();
  }
  async function handleRenumber(event) {
    const nextPropertyKey = `${cityscope || ""}${(map_number || "").toString()}${
      map_number_suffix || ""
    }.${(property_number || "").toString()}${property_number_suffix || ""}${
      unit_number ? `.${(unit_number || "").toString()}${unit_number_suffix || ""}` : ""
    }`;
    const currentPropertyKey = property_key || "";

    const confirmed = window.confirm(
      isNewProperty
        ? `Are you sure you want to create property ${nextPropertyKey}?`
        : `Are you sure you want to renumber ${currentPropertyKey} to ${nextPropertyKey} (including any strata units)?`
    );

    if (!confirmed) {
      return;
    }

    await handleBlur(event);
  }

  function cancelCityscopeReferenceChange() {
    if (property_id != null) {
      setCityscope(orig_cityscope);
      setMapNumber(orig_map_number);
      setMapNumberSuffix(orig_map_number_suffix);
      setPropertyNumber(orig_property_number);
      setPropertyNumberSuffix(orig_property_number_suffix);
      setUnitNumber(orig_unit_number);
      setUnitNumberSuffix(orig_unit_number_suffix);
      setUnitSort(orig_unit_sort);
      setShowCityscopeReference(!showCityscopeReference);
      return;
    }

    navigate("/properties");
  }

  function toggleCityscopeReference() {
    setOrigCityscope(cityscope);
    setOrigMapNumber(map_number);
    setOrigMapNumberSuffix(map_number_suffix);
    setOrigPropertyNumber(property_number);
    setOrigPropertyNumberSuffix(property_number_suffix);
    setOrigUnitNumber(unit_number);
    setOrigUnitNumberSuffix(unit_number_suffix);
    setOrigUnitSort(unit_sort);
    setShowCityscopeReference(!showCityscopeReference);
  }
  async function handleDelete(event) {
    event?.preventDefault?.();

    const confirmed = window.confirm(
      `Are you sure you want to delete the property - ${property_key}?`
    );

    if (!confirmed) {
      return;
    }

    const confirmedFinal = window.confirm(
      `FINAL WARNING. Deleting this property ( ${property_key}) will mean you cannot access it anymore. Do you want to continue?`
    );

    if (!confirmedFinal) {
      return;
    }

    const nextArchivedDate = new Date();
    setArchivedDate(nextArchivedDate);

    const propertySnapshot = syncroniseState();
    const propertyToSave = {
      ...propertySnapshot,
      archived_date: nextArchivedDate,
    };

    setProperty(propertyToSave);
    const result = await saveProperty(propertyToSave);

    if (result?.result === false) {
      alert(result.error_message);
      return;
    }

    navigate("/properties");
  }
  function handleDateChange(date, name) {
    const options = { year: "numeric", month: "long", day: "numeric" };
    const nextValue = date ? new Date(date).toLocaleDateString("en-AU", options) : null;

    if (name === "nla_date") {
      setNlaDate(nextValue);
    }
    if (name === "archived_date") {
      setArchivedDate(nextValue);
    }

    setModified(true);
  }
  const sortBuildingNames = (rows) =>
    [...(rows || [])]
      .sort((a, b) => Number(a?.sort_order ?? 0) - Number(b?.sort_order ?? 0))
      .sort((a, b) => Number(Boolean(a.index_only)) - Number(Boolean(b.index_only)));

  const sortPropertyClassifications = (rows) =>
    [...(rows || [])].sort((a, b) => Number(a?.sort_order ?? 0) - Number(b?.sort_order ?? 0));

  const toggleBuildingNameModal = () => {
    if (showBuildingNameDialog) {
      setBuildingNames((prev) => sortBuildingNames(prev));
    }
    setShowBuildingNameDialog(!showBuildingNameDialog);
  };
  const togglePropertyClassificationModal = () => {
    if (showPropertyClassificationDialog) {
      setPropertyClassifications((prev) => sortPropertyClassifications(prev));
    }
    setShowPropertyClassificationDialog(!showPropertyClassificationDialog);
  };
  const toggleBuildingGradeModal = () => {
    setShowBuildingGradeDialog(!showBuildingGradeDialog);
  };
  function handleEnterOnAgentSearch(e) {
    if (e.keyCode === 13 || e.key === "Enter") {
      e.preventDefault();
      handleAgentSearch();
    }
  }

  async function handleAgentSearch() {
    try {
      if (showAgentDropDown) {
        setShowAgentDropDown(false);
      }

      const agentFilter = {
        name: agentSearchString,
      };

      if (!agentInterstateFlag && state) {
        agentFilter.state = state;
      }

      await getAgents(agentFilter);

      setShowAgentDropDown(true);
      setAgentMenuIsOpen(true);
    } catch {
      alert("Error loading agents");
    }
  }

  async function handleAgentChange(nextSelectedAgent) {
    setSelectedAgentContact({ value: null, label: null });
    setEntityContactIdManagingAgent(null);
    setManagingAgentContact(null);
    setManagingAgentEmail("");

    if (nextSelectedAgent) {
      setSelectedAgent(nextSelectedAgent);
      setEntityIdManagingAgent(nextSelectedAgent.value);
      setManagingAgent(nextSelectedAgent.label);
      setManagingAgentEmail(nextSelectedAgent.email || "");
      setModified(true);

      await getAgentContacts({ entityid: nextSelectedAgent.value });
      setShowAgentContactDropDown(true);
    } else {
      setEntityIdManagingAgent(null);
      setManagingAgent(null);
      setSelectedAgent({ value: null, label: null });
      setEntityContactIdManagingAgent(null);
      setManagingAgentContact(null);
      setShowAgentContactDropDown(false);
      setModified(true);
    }

    setAgentMenuIsOpen(false);
    setTimeout(() => {
      handleBlur();
    }, 0);
  }

  function handleAgentContactChange(nextSelectedAgentContact) {
    if (nextSelectedAgentContact) {
      setEntityContactIdManagingAgent(nextSelectedAgentContact.value);
      setManagingAgentContact(nextSelectedAgentContact.label);
      setSelectedAgentContact(nextSelectedAgentContact);
      setModified(true);
    } else {
      setSelectedAgentContact({ value: null, label: null });
      setEntityContactIdManagingAgent(null);
      setManagingAgentContact(null);
      setModified(true);
    }

    setTimeout(() => {
      handleBlur();
    }, 0);
  }
  function getSelectedUnits() {
    return (strataUnits || []).filter((unit) => unit.selected);
  }

  async function runStrataCopyBatch(action, parameters) {
    const { body } = await post({
      apiName: "colin",
      path: `/properties/${property_id}/strataunits/${action}`,
      options: {
        body: parameters,
      },
    }).response;

    try {
      return await body.json();
    } catch {
      return { result: true };
    }
  }

  async function handleCopyBuildingNamesToStrata(event) {
    event.preventDefault();

    const confirmed = window.confirm(
      "Are you sure you want to copy head record Building Names to the selected units? Note that any existing Building Name records for the selected units will be deleted and replaced as part of the copy process.\n\nNote: This process can take some time. CoLin will prevent you from making further edits until the copy process is complete."
    );

    if (!confirmed) {
      return;
    }

    const selectedUnitIDs = getSelectedUnits().map((unit) => unit.property_id);

    setIsLoading(true);
    try {
      const result = await runStrataCopyBatch("copybuildingname", {
        strataUnitIDs: selectedUnitIDs,
        parameters: {
          created_by: user,
          last_edited_by: user,
        },
      });

      if (result?.result === false) {
        alert(result.error_message);
      }

      alert("Copy complete");
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCopyClassificationsToStrata(event) {
    event.preventDefault();

    const confirmed = window.confirm(
      "Are you sure you want to copy head record Classifications to the selected units? Note that any existing Classification records for the selected units will be deleted and replaced as part of the copy process.\n\nNote: This process can take some time. CoLin will prevent you from making further edits until the copy process is complete."
    );

    if (!confirmed) {
      return;
    }

    const selectedUnitIDs = getSelectedUnits().map((unit) => unit.property_id);

    setIsLoading(true);
    try {
      const result = await runStrataCopyBatch("copyclassification", {
        strataUnitIDs: selectedUnitIDs,
        parameters: {
          created_by: user,
          last_edited_by: user,
        },
      });

      if (result?.result === false) {
        alert(result.error_message);
      }

      alert("Copy complete");
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCopyAddressesToStrata(event) {
    event.preventDefault();

    const confirmed = window.confirm(
      "Are you sure you want to copy head record Street Addresses to the selected units? Note that any existing Street Address records for the selected units will be deleted and replaced as part of the copy process. The address matching for CoreLogic Equivalent Properties will also be re-run for each selected unit.\n\nNote: Suburb+State+Post Code are NOT included in the Street Address copy. Use the \"Copy Suburb+State+PC\" for this function.\n\nNote: This process can take some time. CoLin will prevent you from making further edits until the copy process is complete."
    );

    if (!confirmed) {
      return;
    }

    const selectedUnitIDs = getSelectedUnits().map((unit) => unit.property_id);

    setIsLoading(true);
    try {
      const result = await runStrataCopyBatch("copystreetaddress", {
        strataUnitIDs: selectedUnitIDs,
        parameters: {
          created_by: user,
          last_edited_by: user,
        },
      });

      if (result?.result === false) {
        alert(result.error_message);
      }

      alert("Copy complete");
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCopyPropertySuburb(event) {
    event.preventDefault();

    const suburbStatePostcodePrompt = ` is not set on this Head Record (${property_key}). Copy anyway?`;

    if (
      !window.confirm(
        "Are you sure you want to copy the head record property Suburb, State, and Post Code to the selected units? The address matching for CoreLogic Equivalent Properties will also be re-run for each selected unit.\n\nNote: This process can take some time. CoLin will prevent you from making further edits until the copy process is complete."
      )
    ) {
      return;
    }

    if (!suburb && !window.confirm("Suburb" + suburbStatePostcodePrompt)) {
      return;
    }

    if (!state && !window.confirm("State" + suburbStatePostcodePrompt)) {
      return;
    }

    if (!postcode && !window.confirm("Postcode" + suburbStatePostcodePrompt)) {
      return;
    }

    const selectedUnitIDs = getSelectedUnits().map((unit) => unit.property_id);

    setIsLoading(true);
    try {
      const result = await runStrataCopyBatch("copysuburb", {
        strataUnitIDs: selectedUnitIDs,
        parameters: {
          last_edited_by: user,
          suburb,
          state,
          postcode,
        },
      });

      if (result?.result === false) {
        alert(result.error_message);
      }

      alert("Copy complete");
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCopyOwnersToStrata(event) {
    event.preventDefault();

    const confirmed = window.confirm(
      "Are you sure you want to copy head record Property Owner(s) to the selected units? Please Note: Should any of the selected Units have existing Property Owner(s), you will be prompted to make the decision to overwrite or retain the pre-existing Property Owner(s).\n\nNote: This process can take some time. CoLin will prevent you from making further edits until the copy process is complete."
    );

    if (!confirmed) {
      return;
    }

    const selectedUnits = getSelectedUnits();
    if (selectedUnits.length === 0) {
      alert("Please select at least one Strata Unit before copying owners.");
      return;
    }

    const unitsToUpdate = [];
    for (let i = 0; i < selectedUnits.length; i++) {
      const unit = selectedUnits[i];
      const ownerCountRaw = unit?.owner_count;
      const ownerCountParsed = Number.parseInt(String(ownerCountRaw ?? "").trim(), 10);
      const hasExistingOwners = Number.isFinite(ownerCountParsed)
        ? ownerCountParsed > 0
        : Boolean(ownerCountRaw);

      if (hasExistingOwners) {
        const confirmOverwrite = window.confirm(
          `Unit: ${unit.property_key} has owners linked to the property. Click OK to overwrite the existing Property Owner(s) for this unit, or Cancel to skip the copy for this unit, and retain the pre-existing Property Owner(s).`
        );
        if (confirmOverwrite) {
          unitsToUpdate.push(unit.property_id);
        }
      } else {
        unitsToUpdate.push(unit.property_id);
      }
    }

    const selectedUnitIDs = selectedUnits
      .filter((unit) => unitsToUpdate.includes(unit.property_id))
      .map((unit) => unit.property_id);

    if (selectedUnitIDs.length === 0) {
      alert("No units were selected for owner overwrite. Copy cancelled.");
      return;
    }

    setIsLoading(true);
    try {
      const result = await runStrataCopyBatch("copyowner", {
        strataUnitIDs: selectedUnitIDs,
        parameters: {
          created_by: user,
          last_edited_by: user,
        },
      });

      if (result?.result === false) {
        alert(result.error_message);
      }

      alert("Copy complete");
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCopyParcelsToStrata(event) {
    event.preventDefault();

    const confirmed = window.confirm(
      "Are you sure you want to copy head record Parcels to the selected units? Note that any existing unit record Parcels will be deleted and replaced as part of the copy process. The unit record Unit Number will automatically be allocated as the Lot Number for the Parcel for each strata unit record.\n\nNote: This process can take some time. CoLin will prevent you from making further edits until the copy process is complete."
    );

    if (!confirmed) {
      return;
    }

    const selectedUnitIDs = getSelectedUnits().map((unit) => unit.property_id);

    setIsLoading(true);
    try {
      const result = await runStrataCopyBatch("copytitlereference", {
        strataUnitIDs: selectedUnitIDs,
        parameters: {
          created_by: user,
          last_edited_by: user,
        },
      });

      if (result?.result === false) {
        alert(result.error_message);
      }

      alert("Copy complete");
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    } finally {
      setIsLoading(false);
    }
  }
  function toggleOwnerSearchDialog() {
    setShowOwnerSearchDialog((prev) => {
      const next = !prev;
      // When closing the dialog, refresh property owners
      if (!next) {
        refreshPropertyOwners();
      }
      return next;
    });
  }

  async function refreshPropertyOwners() {
    try {
      const { body } = await get({
        apiName: "colin",
        path: "/propertyowners",
        options: { queryParams: { propertyid: property_id } },
      }).response;
      const result = await body.json();
      const owners = Array.isArray(result) ? result : result.propertyowners || [];
      setPropertyOwners(owners);
    } catch (error) {
      console.error("Error refreshing property owners:", error);
    }
  }
  async function handleCopyPropertyOwners() {
    if (!copyPropertyOwner) {
      alert("Enter a cityscope reference to copy owners from");
      return;
    }
    const confirmed = window.confirm(`Are you sure you want to copy owners from ${copyPropertyOwner}?`);
    if (!confirmed) return;

    try {
      const { body } = await post({
        apiName: "colin",
        path: `/property/${property_id}/propertyowners/${copyPropertyOwner}/${copyPropertyOwnerAndDeleteFlag}`,
        options: {
          body: {
            property_id,
            created_by: user,
            last_edited_by: user,
          },
        },
      }).response;
      const result = await body.json();
      if (result && result.result === false) {
        alert(result.error_message);
      } else {
        await refreshPropertyOwners();
      }
    } catch (error) {
      alert("Error copying owners. Please try again.");
      console.error(error);
    }
  }
  async function handleCityscopeChange(selectedOption) {
    const nextCityscope = selectedOption?.value || null;
    const nextFilter = nextCityscope ? { cityscope: nextCityscope } : null;

    setSelectedCityscope(selectedOption || null);
    setCityscope(nextCityscope);
    setSuburbFilter(nextFilter);
    setModified(true);

    if (nextFilter) {
      await getSuburbs(nextFilter);
      await getStreets(nextFilter);
    }
  }

  function handleCheckBoxChange(event) {
    const { name, checked } = event.target;
    const checkboxSetters = {
      sub_divided_unit: setSubDividedUnit,
      parking_yes_no: setParkingYesNo,
      photo_required: setPhotoRequired,
      agentInterstateFlag: setAgentInterstateFlag,
      copyPropertyOwnerAndDeleteFlag: setCopyPropertyOwnerAndDeleteFlag,
    };

    if (checkboxSetters[name]) {
      checkboxSetters[name](checked);
      setModified(true);
      pendingCheckboxSaveRef.current = true;
    }

    if (name === "sub_divided_unit" && checked) {
      alert(
        "NOTE: The idea behind flagging a unit record as a 'subdivided unit' is so it will be excluded from proofing queries. The record will appear to the customer in both hard copy and database products."
      );
    }
  }

  // --- Modal Functions for Building Names, Classifications, and Grades ---
  
  /**
   * Updates table state when a cell is edited
   * @param {number} rowIndex - Index of the row
   * @param {string} columnId - Column ID being edited
   * @param {any} value - New value for the cell
   */
  const dataSetSetters = {
    properties: setProperties,
    building_names: setBuildingNames,
    property_classifications: setPropertyClassifications,
    building_grades: setBuildingGrades,
    addresses: setAddresses,
    streetOptions: setStreetOptions,
    integrations: setIntegrations,
    titleDetails: setTitleDetails,
    volumeFolios: setVolumeFolios,
    strataUnits: setStrataUnits,
    queries: setQueries,
    propertySales: setPropertySales,
    propertySaleSheets: setPropertySaleSheets,
    propertyOwners: setPropertyOwners,
    propertyArticles: setPropertyArticles,
    withholdings: setWithholdings,
    liftOperators: setLiftOperators,
    nabers: setNabers,
    leases: setLeases,
    leasingAgents: setLeasingAgents,
    occupancyRates: setOccupancyRates,
    propertyProposals: setPropertyProposals,
  };

  // Called from addRecord in child lists as updateTableState(newDataArray, dataSetName)
  // Called from EditableCell tableMeta.updateData as updateTableState(dataSetName, rowIndex, columnId, value)
  const updateTableState = (rowIndexOrData, columnIdOrSet, value, nextValue) => {
    if (Array.isArray(rowIndexOrData)) {
      // addRecord path: first arg is the full updated array, second is the dataset key
      const setter = dataSetSetters[columnIdOrSet];
      if (setter) {
        setter(rowIndexOrData);
      } else {
        console.warn(`updateTableState: unknown dataSet "${columnIdOrSet}"`);
      }
    } else if (typeof rowIndexOrData === "string") {
      // editable-cell path: first arg is dataset key
      const dataSet = rowIndexOrData;
      const rowIndex = columnIdOrSet;
      const columnId = value;
      const setter = dataSetSetters[dataSet];

      if (setter) {
        setter((prev) => {
          const updated = [...prev];
          updated[rowIndex] = {
            ...updated[rowIndex],
            [columnId]: nextValue,
            modified: true,
          };
          return updated;
        });
      } else {
        console.warn(`updateTableState: unknown dataSet "${dataSet}"`);
      }
    } else {
      console.warn("updateTableState called without a dataset context", {
        rowIndexOrData,
        columnIdOrSet,
        value,
        nextValue,
      });
    }
  };

  const updateLeasingAgentsTableState = (indexOrNewData, columnIdOrDataSet, value) => {
    if (Array.isArray(indexOrNewData)) {
      setLeasingAgents(indexOrNewData);
      return;
    }

    const rowIndex = indexOrNewData;
    setLeasingAgents((prev) => {
      const next = [...prev];
      if (!next[rowIndex]) {
        return prev;
      }

      next[rowIndex] = {
        ...next[rowIndex],
        [columnIdOrDataSet]: value,
        modified: true,
      };
      return next;
    });
  };

  const updateLiftOperatorsTableState = (indexOrNewData, columnIdOrDataSet, value) => {
    if (Array.isArray(indexOrNewData)) {
      setLiftOperators(indexOrNewData);
      return;
    }

    const rowIndex = indexOrNewData;
    setLiftOperators((prev) => {
      const next = [...prev];
      if (!next[rowIndex]) {
        return prev;
      }

      next[rowIndex] = {
        ...next[rowIndex],
        [columnIdOrDataSet]: value,
        modified: true,
      };
      return next;
    });
  };

  const saveLeasingAgentRecord = async (record, _dataSet, idKey = "leasing_agent_id") => {
    return saveRecord("leasingAgents", record, idKey);
  };

  const saveLiftOperatorRecord = async (record, _dataSet, idKey = "lift_operator_id") => {
    return saveRecord("liftOperators", record, idKey);
  };

  const saveAddressesRecord = async (record, overrides = {}) => {
    setAddressSaved(true);

    const nextSuburb = overrides.suburb ?? suburb;
    const nextState = overrides.state ?? state;
    const nextPostcode = overrides.postcode ?? postcode;

    const addressRecord = {
      ...record,
      suburb: nextSuburb,
      state: nextState,
      postcode: nextPostcode,
      unit_number: record.unit_number ?? unit_number,
      unit_number_suffix: record.unit_number_suffix ?? unit_number_suffix,
      property_key: record.property_key || property_key,
      last_edited_by: user,
    };

    return saveRecord("addresses", addressRecord);
  };

  /**
   * Saves a record to the API
   * @param {object} record - The record object to save
   * @param {string} dataSet - Type of data (e.g., 'addresses', 'propertySales')
   * @param {string} id - Primary key field name (e.g., 'street_address_id', 'property_sale_id')
   */
  const saveRecord = async (dataSet, record) => {
    const dataSetConfig = {
      addresses:                { apiPath: 'addresses',              primaryKey: 'street_address_id' },
      integrations:             { apiPath: 'integrations',           primaryKey: 'property_integration_id' },
      building_names:           { apiPath: 'buildingnames',           primaryKey: 'building_name_id' },
      property_classifications: { apiPath: 'propertyclassifications', primaryKey: 'property_classification_id' },
      building_grades:          { apiPath: 'buildinggrades',          primaryKey: 'building_grade_id' },
      titleDetails:             { apiPath: 'parcels',                 primaryKey: 'parcel_id' },
      volumeFolios:             { apiPath: 'volume_folios',           primaryKey: 'volume_folio_id' },
      queries:                  { apiPath: 'queries',                 primaryKey: 'query_id' },
      withholdings:             { apiPath: 'withholdings',            primaryKey: 'property_withholding_id' },
      occupancyRates:           { apiPath: 'occupancyrates',          primaryKey: 'occupancy_rate_id' },
      propertyProposals:        { apiPath: 'propertyproposals',       primaryKey: 'property_proposal_id' },
      propertySaleSheets:       { apiPath: 'propertysalesheets',      primaryKey: 'property_sale_sheet_id' },
      propertyArticles:         { apiPath: 'propertyarticles',        primaryKey: 'article_property_link_id' },
      liftOperators:            { apiPath: 'liftoperators',            primaryKey: 'lift_operator_id' },
      strataUnits:              { apiPath: 'strataunits',             primaryKey: 'property_id' },
      nabers:                   { apiPath: 'nabers',                  primaryKey: 'nabers_id' },
      leasingAgents:            { apiPath: 'leasingagents',            primaryKey: 'leasing_agent_id' },
      propertySales:            { apiPath: 'propertysales',           primaryKey: 'property_sale_id' },
    };

    // Many-to-many relationship: uses direct endpoint /propertyowners/{id}
    if (dataSet === "propertyOwners" || dataSet === "propertyowners") {
      const isNew = record.new_record === true || !record.property_owner_id;
      const requestBody = { ...record, last_edited_by: user };
      const { body } = isNew
        ? await post({
            apiName: "colin",
            path: "/propertyowners/new",
            options: { body: requestBody },
          }).response
        : await put({
            apiName: "colin",
            path: `/propertyowners/${record.property_owner_id}`,
            options: { body: requestBody },
          }).response;
      const result = await body.json();
      const setter = dataSetSetters.propertyOwners;
      const savedRecord = {
        ...requestBody,
        ...result,
        property_owner_id: result?.id || result?.property_owner_id || record.property_owner_id,
        new_record: false,
        modified: false,
      };
      if (setter && result) {
        setter(prev =>
          prev.map(item =>
            ((isNew && String(item.property_owner_id) === String(record.property_owner_id)) ||
              (!isNew && String(item.property_owner_id) === String(record.property_owner_id)))
              ? savedRecord
              : item
          )
        );
      }
      return result;
    }

    try {
      const config = dataSetConfig[dataSet];
      if (!config) throw new Error(`Unknown dataSet: ${dataSet}`);

      const isNew = record.new_record === true || !record[config.primaryKey];
      const isTopLevelDataSet = dataSet === "propertyProposals" || dataSet === "propertySaleSheets" || dataSet === "propertyArticles" || dataSet === "propertySales";
      const basePath = isTopLevelDataSet
        ? `/${config.apiPath}`
        : `/properties/${property_id}/${config.apiPath}`;
      const apiPath = isNew ? `${basePath}/new` : `${basePath}/${record[config.primaryKey]}`;
      const method = isNew ? post : put;

      const requestBody = dataSet === "addresses"
        ? {
            ...record,
            suburb: record.suburb ?? suburb,
            state: record.state ?? state,
            postcode: record.postcode ?? postcode,
            unit_number: record.unit_number ?? unit_number,
            unit_number_suffix: record.unit_number_suffix ?? unit_number_suffix,
            property_key: record.property_key || property_key,
            last_edited_by: user,
          }
        : {
            ...record,
            last_edited_by: user,
          };

      const { body } = await method({
        apiName: "colin",
        path: apiPath,
        options: { body: requestBody },
      }).response;
      const result = await body.json();

      // Merge returned data (e.g. server-assigned ID) back into local state
      const setter = dataSetSetters[dataSet];
      const savedRecord = {
        ...requestBody,
        ...result,
        [config.primaryKey]: result?.id || result?.[config.primaryKey] || record[config.primaryKey],
        new_record: false,
        modified: false,
      };

      if (setter && result) {
        setter(prev => {
          const next = prev.map(item => {
            const matchesNew = isNew && item._client_id && item._client_id === record._client_id;
            const matchesSaved = !isNew && String(item[config.primaryKey]) === String(record[config.primaryKey]);
            if (!matchesNew && !matchesSaved) {
              return item;
            }
            return {
              ...item,
              ...savedRecord,
              [config.primaryKey]: savedRecord[config.primaryKey] ?? item[config.primaryKey],
            };
          });

          if (dataSet === "building_names") {
            return sortBuildingNames(next);
          }

          if (dataSet === "property_classifications") {
            return sortPropertyClassifications(next);
          }

          return next;
        });
      }

      return result;
    } catch (error) {
      console.error(`Error saving ${dataSet}:`, error);
      alert(`Failed to save ${dataSet}: ${error.message}`);
      throw error;
    }
  };

  /**
   * Deletes a record via the API
   * @param {string} dataSet - Type of data (e.g., 'addresses', 'building_names')
   * @param {object} record - The record object to delete
   */
  const deleteRecord = async (dataSet, record) => {
    const dataSetConfig = {
      addresses:                { apiPath: 'addresses',              primaryKey: 'street_address_id' },
      integrations:             { apiPath: 'integrations',           primaryKey: 'property_integration_id' },
      building_names:           { apiPath: 'buildingnames',           primaryKey: 'building_name_id' },
      property_classifications: { apiPath: 'propertyclassifications', primaryKey: 'property_classification_id' },
      building_grades:          { apiPath: 'buildinggrades',          primaryKey: 'building_grade_id' },
      titleDetails:             { apiPath: 'parcels',                 primaryKey: 'parcel_id' },
      volumeFolios:             { apiPath: 'volume_folios',           primaryKey: 'volume_folio_id' },
      queries:                  { apiPath: 'queries',                 primaryKey: 'query_id' },
      withholdings:             { apiPath: 'withholdings',            primaryKey: 'property_withholding_id' },
      occupancyRates:           { apiPath: 'occupancyrates',          primaryKey: 'occupancy_rate_id' },
      propertyProposals:        { apiPath: 'propertyproposals',       primaryKey: 'property_proposal_id' },
      propertySaleSheets:       { apiPath: 'propertysalesheets',      primaryKey: 'property_sale_sheet_id' },
      propertyArticles:         { apiPath: 'propertyarticles',        primaryKey: 'article_property_link_id' },
      strataUnits:              { apiPath: 'strataunits',             primaryKey: 'property_id' },
      nabers:                   { apiPath: 'nabers',                  primaryKey: 'nabers_id' },
    };

    try {
      const config = dataSetConfig[dataSet];
      if (!config) {
        throw new Error(`Unknown dataSet: ${dataSet}`);
      }

      const setter = dataSetSetters[dataSet];
      const recordId = record[config.primaryKey] ?? record.id ?? null;

      if (!recordId) {
        if (setter) {
          setter(prev => prev.filter(item => item._client_id !== record._client_id));
        }
        return { result: true };
      }

      const response = await saveRecord(dataSet, {
        ...record,
        archived_date: new Date(),
        last_edited_by: user,
        modified: true,
      });

      if (setter) {
        setter(prev => prev.filter(item => String(item[config.primaryKey]) !== String(recordId)));
      }

      return response;
    } catch (error) {
      console.error(`Error deleting ${dataSet}:`, error);
      alert(`Failed to delete ${dataSet}: ${error.message}`);
      throw error;
    }
  };

  /**
   * Validates if a record can be deleted or modified
   * @returns {[boolean, string|null]} - Validation result and optional message
   */
  const validateRelatedRecord = (record, setName, relatedRecords = liftOperators) => {
    if (setName === "addresses") {
      const resolvedAddresses = addresses.map((address) => {
        const currentKey = address.street_address_id ?? address._client_id;
        const recordKey = record.street_address_id ?? record._client_id;

        return currentKey === recordKey ? { ...address, ...record } : address;
      });
      const addressesForValidation =
        resolvedAddresses.some((address) => (address.street_address_id ?? address._client_id) === (record.street_address_id ?? record._client_id))
          ? resolvedAddresses
          : [...resolvedAddresses, record];

      if (!record.street_name || String(record.street_name).trim() === "") {
        return [false, "ALERT!: Street name cannot be blank. Changes to this record WILL NOT BE SAVED until fixed."];
      }

      if (record.main_address === true) {
        const mainAddressCount = addressesForValidation.reduce(
          (count, address) => count + (address.main_address ? 1 : 0),
          0
        );

        if (mainAddressCount > 1) {
          return [false, "ALERT!: Only one main address is allowed. Changes to this record WILL NOT BE SAVED until fixed."];
        }
      }

      const sortOrderCounts = addressesForValidation.reduce((counts, address) => {
        const sortOrder = address.sort_order;
        if (sortOrder !== null && sortOrder !== undefined && sortOrder !== "") {
          counts[sortOrder] = (counts[sortOrder] || 0) + 1;
        }
        return counts;
      }, {});

      if (Object.values(sortOrderCounts).some((count) => count > 1)) {
        return [false, "ALERT!: Duplicate sort orders. Changes to this record WILL NOT BE SAVED until fixed."];
      }

      return [true, null];
    }

    if (setName === "property") {
      if (!cityscope || !map_number || !property_number) {
        return [false, "Entries are required for Cityscope, Map No and Property No fields."];
      }

      if (unit_number && !unit_sort) {
        return [false, "Unit sort is required for unit entries."];
      }

      if (email) {
        const emailRegex =
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (!emailRegex.test(email)) {
          return [false, "The email is not a correct format"];
        }
      }

      if (internet) {
        const urlRegex = /^[\w.-]{3}(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/;
        if (!urlRegex.test(internet)) {
          return [false, "The url (website) is not a correct format. Please enter www.{}.{}"];
        }
      }
    }

    if (setName === "leases" && record.registered && !record.lease_number) {
      return [false, "Enter a lease number for lease id - " + record.lease_id];
    }

    if (["leasingAgent", "leasingAgents"].includes(setName) && !record.entity_id_leasing_agent) {
      return [false, "You must select a leasing agent. The record has not been saved"];
    }

    if (setName === "occupancyRates" && record.occupancy_percent != null) {
      if (record.occupancy_percent < 0 || record.occupancy_percent > 100) {
        return [false, "The occupancy percent must be between 0 and 100"];
      }
    }

    if (setName === "liftOperators") {
      const sortOrderCounts = relatedRecords.reduce((counts, operator) => {
        const sortOrder = operator.sort_order;
        if (!operator.archived_date && sortOrder != null && String(sortOrder).trim() !== "") {
          const key = String(sortOrder).trim();
          counts[key] = (counts[key] || 0) + 1;
        }
        return counts;
      }, {});

      if (Object.values(sortOrderCounts).some(count => count > 1)) {
        return [false, "Duplicate sort orders. The record has not been saved"];
      }

      if (record.entity_id_lift_operator != null && state && record.name && !record.name.includes(state)) {
        return [true, "The lift operator selected is in another state"];
      }
    }

    if (setName === "strataUnits") {
      if (record.unit_sort === null || record.unit_sort === undefined || String(record.unit_sort).trim() === "") {
        return [false, "Unit Sort cannot be blank. The record has not been saved"];
      }

      const resolvedStrataUnits = (strataUnits || []).map((unit) => {
        const currentKey = unit.property_id ?? unit._client_id;
        const recordKey = record.property_id ?? record._client_id;

        return currentKey === recordKey ? { ...unit, ...record } : unit;
      });

      const unitsForValidation = resolvedStrataUnits.some(
        (unit) => (unit.property_id ?? unit._client_id) === (record.property_id ?? record._client_id)
      )
        ? resolvedStrataUnits
        : [...resolvedStrataUnits, record];

      const unitSortCounts = unitsForValidation.reduce((counts, unit) => {
        const unitSort = unit?.unit_sort;
        if (unitSort !== null && unitSort !== undefined && String(unitSort).trim() !== "") {
          const key = String(unitSort).trim();
          counts[key] = (counts[key] || 0) + 1;
        }
        return counts;
      }, {});

      if (Object.values(unitSortCounts).some((count) => count > 1)) {
        return [false, "Duplicate unit sorts. The record has not been saved"];
      }
    }

    return [true, null];
  };

  async function saveLeaseRecord(recordOrBatch, _dataSet = "leases", idKey = "lease_id") {
    if (Array.isArray(recordOrBatch)) {
      for (const record of recordOrBatch) {
        const result = await saveLeaseRecord(record, "leases", idKey);
        if (result?.result === false) {
          return result;
        }
      }

      return { result: true };
    }

    const record = recordOrBatch;
    const effectivePropertyId = property_id || record?.property_id;
    if (!effectivePropertyId) {
      return { result: false, error_message: "Missing property id for lease save." };
    }

    const isNew = record?.new_record === true || !record?.[idKey];
    const path = isNew
      ? `/properties/${effectivePropertyId}/leases/new`
      : `/properties/${effectivePropertyId}/leases/${record[idKey]}`;
    const method = isNew ? post : put;

    try {
      const { body } = await method({
        apiName: "colin",
        path,
        options: {
          body: {
            ...record,
            last_edited_by: user,
          },
        },
      }).response;

      return await body.json();
    } catch (error) {
      return { result: false, error_message: error?.message || "Failed to save lease." };
    }
  }

  const propertyKeyValue = String(property_key ?? "");
  const liveDataRef = propertyKeyValue.replace(".0", ".");
  return (
    <div className="Property">
      {isLoading && (
        <div className="outer-div">
          <div className="sweet-loading inner-div">
            <PacmanLoader
              cssOverride={{ display: "block", margin: "0 auto" }}
              size={20}
              color={"#e03c31"}
              loading={isLoading}
            />
          </div>
        </div>
      )}
      {showOwnerSearchDialog && (
        <OwnerSearchModal
          isAuthenticated={isAuthenticated}
          user={user}
          toggleOwnerSearchDialog={toggleOwnerSearchDialog}
          properties={[{ property_key: property_key }]}
          show={showOwnerSearchDialog}
        />
      )}
      {showAddressDialog && (
        <AddressModal
          show={showAddressDialog}
          onClose={toggleAddressModal}
          filter={addressFilter}
          isAuthenticated={isAuthenticated}
          toggleAddressModal={toggleAddressModal}
          user={user}
          data={addresses}
          updateTableState={updateTableState}
          saveRecord={saveAddressesRecord}
          deleteRecord={deleteRecord}
          validateRelatedRecord={validateRelatedRecord}
          streetOptions={streetOptions}
        />
      )}

      {showAddressMatchingDialog && (
        <PropertyIntegrationsModal
          show={showAddressMatchingDialog}
          onClose={togglePropertyIntegrationsModal}
          filter={integrationFilter}
          isAuthenticated={isAuthenticated}
          user={user}
          data={integrations}
          updateTableState={updateTableState}
          saveRecord={saveRecord}
          deleteRecord={deleteRecord}
          acceptIntegration={acceptIntegration}
          rejectIntegration={rejectIntegration}
          ctx="cs"
        />
      )}

      {showTitleDetailsDialog && (
        <TitleDetailsModal
          show={showTitleDetailsDialog}
          onClose={toggleTitleDetailsModal}
          filter={addressFilter}
          isAuthenticated={isAuthenticated}
          toggleTitleDetailsModal={toggleTitleDetailsModal}
          user={user}
          data={titleDetails}
          dataFetch={getTitleDetails}
          updateTableState={updateTableState}
          saveRecord={saveRecord}
          deleteRecord={deleteRecord}
          validateRelatedRecord={validateRelatedRecord}
          stateCode={state}
        />
      )}

      {showVolumeFoliosDialog && (
        <VolumeFoliosModal
          show={showVolumeFoliosDialog}
          onClose={toggleVolumeFoliosModal}
          filter={addressFilter}
          isAuthenticated={isAuthenticated}
          toggleVolumeFoliosModal={toggleVolumeFoliosModal}
          user={user}
          data={volumeFolios}
          dataFetch={getVolumeFolios}
          updateTableState={updateTableState}
          saveRecord={saveRecord}
          deleteRecord={deleteRecord}
          validateRelatedRecord={validateRelatedRecord}
        />
      )}

      {showWithholdingDialog && (
        <WithholdingsModal
          show={showWithholdingDialog}
          onClose={toggleWithholdingModal}
          filter={addressFilter}
          isAuthenticated={isAuthenticated}
          toggleWithholdingModal={toggleWithholdingModal}
          user={user}
          data={withholdings}
          updateTableState={updateTableState}
          saveRecord={saveRecord}
          bulkSaveRecords={bulkSaveWithholdings}
          refreshRecords={refreshWithholdings}
          deleteRecord={deleteRecord}
          validateRelatedRecord={validateRelatedRecord}
          withholdingReasons={withholdingReasons}
          userPermissions={effectiveUserPermissions}
        />
      )}

      {showPropertyAddDialog && (
        <PropertyAddModal
          show={showPropertyAddDialog}
          onClose={togglePropertyAddModal}
          filter={addressFilter}
          isAuthenticated={isAuthenticated}
          togglePropertyAddModal={togglePropertyAddModal}
          user={user}
          data={properties}
          updateTableState={updateTableState}
          saveRecord={saveRecord}
          deleteRecord={deleteRecord}
          validateRelatedRecord={validateRelatedRecord}
          cityscopeOptions={cityscopeOptions}
          propertyid={property_id}
          suburbOptions={suburbOptions}
        />
      )}

      {showBuildingNameDialog && (
        <BuildingNameModal
          show={showBuildingNameDialog}
          onClose={toggleBuildingNameModal}
          filter={buildingNameFilter}
          isAuthenticated={isAuthenticated}
          toggleBuildingNameModal={toggleBuildingNameModal}
          user={user}
          data={buildingNames}
          updateTableState={updateTableState}
          saveRecord={saveRecord}
          deleteRecord={deleteRecord}
          validateRelatedRecord={validateRelatedRecord}
        />
      )}
      {showPropertyClassificationDialog && (
        <PropertyClassificationModal
          show={showPropertyClassificationDialog}
          onClose={togglePropertyClassificationModal}
          filter={propertyClassificationFilter}
          isAuthenticated={isAuthenticated}
          togglePropertyClassificationModal={togglePropertyClassificationModal}
          user={user}
          data={propertyClassifications}
          updateTableState={updateTableState}
          saveRecord={saveRecord}
          deleteRecord={deleteRecord}
          classificationOptions={lookupClassificationOptions}
          validateRelatedRecord={validateRelatedRecord}
        />
      )}
      {showBuildingGradeDialog && (
        <BuildingGradeModal
          show={showBuildingGradeDialog}
          onClose={toggleBuildingGradeModal}
          filter={buildingGradeFilter}
          isAuthenticated={isAuthenticated}
          toggleBuildingGradeModal={toggleBuildingGradeModal}
          user={user}
          data={buildingGrades}
          updateTableState={updateTableState}
          saveRecord={saveRecord}
          deleteRecord={deleteRecord}
          buildingGradeSourceOptions={lookupBuildingGradeSourceOptions}
          buildingGradeOptions={lookupBuildingGradeOptions}
          validateRelatedRecord={validateRelatedRecord}
        />
      )}

      {property && isAuthenticated && !isLoading && (
        <div className="PropertyForm">
          <Form>
            <div className="d-flex align-items-center w-100 mb-2" style={{ gap: "0.5rem" }}>
              <div style={{ flex: "0 0 auto", maxWidth: "33%" }}>
                <h6 className="text-nowrap mb-0">
                  Property Details - {property_key} |{" "}
                  {addresses.length > 0 ? addresses[0].street_address : ""} |{" "}
                  {buildingNames.length > 0 ? buildingNames[0].building_name : ""}{" "}
                  {archived_date && <span style={{ color: "red" }}> || *** ARCHIVED ***</span>}
                </h6>
              </div>
              <div className="d-flex justify-content-center" style={{ flex: "1" }}>
                <Nav className="justify-content-center align-items-center">
                  {previousId !== null && idList.length > 0 && (
                    <Nav.Item>
                      <Button
                        type="button"
                        variant="info"
                        size="sm"
                        onClick={() => navigateToProperty(previousId, idList, showIndexNavigation)}
                      >
                        Previous
                      </Button>{" "}
                    </Nav.Item>
                  )}

                  {nextId !== null && idList.length > 0 && (
                    <Nav.Item>
                      <Button
                        type="button"
                        variant="info"
                        size="sm"
                        onClick={() => navigateToProperty(nextId, idList, showIndexNavigation)}
                      >
                        Next
                      </Button>{" "}
                    </Nav.Item>
                  )}

                  {previous_property_id && idList.length === 0 && (
                    <Nav.Item>
                      <Button
                        type="button"
                        variant="info"
                        size="sm"
                        onClick={() => navigateToProperty(previous_property_id, [], false)}
                      >
                        Previous
                      </Button>{" "}
                    </Nav.Item>
                  )}
                  {next_property_id && idList.length === 0 && (
                    <Nav.Item>
                      <Button
                        type="button"
                        variant="info"
                        size="sm"
                        onClick={() => navigateToProperty(next_property_id, [], false)}
                      >
                        Next
                      </Button>{" "}
                    </Nav.Item>
                  )}

                  {currentIdIndex !== null && idList.length > 0 && (
                    <Form.Group as={Row} controlId="currentIdIndex" className="align-items-center mb-0">
                      <Col className="zeroPadRight">
                        <Form.Control
                          onChange={handleCurrentIdIndexChange}
                          value={currentIdIndex + 1}
                          as="input"
                          type="text"
                          min="0"
                          className="pageCount"
                          onBlur={handleGoToRecord}
                          onKeyDown={handleEnterCurrentIdIndex}
                        />
                      </Col>
                      <Col className="zeroPadLeft">
                        <h6 className="topPad mb-0">of {idList.length} </h6>
                      </Col>
                    </Form.Group>
                  )}
                  {currentIdIndex !== null && idList.length > 0 && (
                    <NavDropdown title="Go To Cityscope Reference">
                      {idList.map((r) => (
                        <NavDropdown.Item
                          key={r}
                          eventKey={r}
                          onClick={() => navigateToProperty(r, idList, showIndexNavigation)}
                        >
                          {r}
                        </NavDropdown.Item>
                      ))}
                    </NavDropdown>
                  )}
                </Nav>
              </div>
              <div className="d-flex justify-content-end" style={{ flex: "0 0 auto" }}>
                <Nav className="justify-content-end ">
                  {withholdings.length > 0 && (
                    <Nav.Item>
                      <Button variant="danger" type="button" onClick={toggleWithholdingModal} size="sm">
                        On Hold
                      </Button>{" "}
                    </Nav.Item>
                  )}
                  <Nav.Item>
                    <Button
                      variant="primary"
                      type="button"
                      size="sm"
                      onClick={togglePropertyAddModal}
                    >
                      Bulk Add Properties
                    </Button>{" "}
                  </Nav.Item>
                  <Nav.Item>
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href={`https://www.cityscopeonline.com.au/Logon.aspx?autolog=ThdvAU+ei+Y=&ref=${liveDataRef}`}
                    >
                      <Button size="sm" variant="primary" type="button">
                        View Live Data
                      </Button>
                    </a>{" "}
                  </Nav.Item>
                  <Nav.Item>
                    <a href={`/properties/${propertyKeyValue}/previewtext`} target="_blank" rel="noreferrer">
                      <Button variant="primary" size="sm" type="button">
                        Preview Text
                      </Button>
                    </a>{" "}
                  </Nav.Item>
                  <Nav.Item>
                    <Button variant="primary" type="button" onClick={handleHold} size="sm">
                      Hold Property
                    </Button>
                  </Nav.Item>
                </Nav>
              </div>
            </div>

            <Form.Group as={Form.Row} id="propertytabs">
              <Col>
                <Modal
                  show={showAlert}
                  onHide={(e) => {
                    setShowAlert(!showAlert);
                  }}
                  centered
                >
                  <Modal.Header closeButton>
                    {" "}
                    <Modal.Title>Uh Oh!</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    {" "}
                    <Alert variant="danger">{errorMessage}</Alert>{" "}
                  </Modal.Body>
                </Modal>

                <Tabs defaultActiveKey={0} activeKey={tabKey} id="tabset" onSelect={onTabSelect}>
                  <Tab eventKey={15} title="Address Details" tabAttrs={getPropertyTabAttrs(15)}>
                    <Card>
                      <Card.Body>
                        <Row>
                          <Col sm={12} md={6} lg={3}>
                            <Card>
                              <Card.Header>Addresses</Card.Header>
                              <Card.Body>
                                {!showCityscopeReference && (
                                  <Card>
                                    <Card.Body>
                                      <Button
                                        id="property-address-edit-button"
                                        onClick={toggleAddressModal}
                                        size="sm"
                                        variant="secondary"
                                        type="button"
                                        disabled={head_record_property_id ? true : false}
                                      >
                                        Edit
                                      </Button>
                                      <AddressList
                                        filter={addressFilter}
                                        isAuthenticated={isAuthenticated}
                                        controlType="list"
                                        data={addresses}
                                        updateTableState={updateTableState}
                                        saveRecord={saveAddressesRecord}
                                        deleteRecord={deleteRecord}
                                        user={user}
                                      />
                                    </Card.Body>
                                  </Card>
                                )}
                                <Form.Group as={Row} controlId="suburb">
                                  <Form.Label
                                    as={Col}
                                    className="text-right"
                                    column
                                    sm={6}
                                    md={6}
                                    lg={4}
                                  >
                                    Suburb
                                  </Form.Label>
                                  <Col>
                                    <Select
                                      value={selectedSuburb}
                                      onChange={handleSuburbChange}
                                      options={suburbOptions}
                                      onInputChange={handleInputChange}
                                      onBlur={handleBlur}
                                      isDisabled={head_record_property_id ? true : false}
                                    />
                                  </Col>
                                </Form.Group>
                                <Form.Group as={Row} controlId="state">
                                  <Form.Label
                                    as={Col}
                                    className="text-right"
                                    column
                                    sm={6}
                                    md={6}
                                    lg={4}
                                  >
                                    State
                                  </Form.Label>
                                  <Col>
                                    <Form.Control
                                      onChange={handleChange}
                                      value={state ?? ""}
                                      as="input"
                                      type="text"
                                      readOnly
                                    />
                                  </Col>
                                </Form.Group>
                                <Form.Group as={Row} controlId="postcode">
                                  <Form.Label
                                    as={Col}
                                    className="text-right"
                                    column
                                    sm={6}
                                    md={6}
                                    lg={4}
                                  >
                                    Postcode
                                  </Form.Label>
                                  <Col>
                                    <Form.Control
                                      onChange={handleChange}
                                      value={postcode ?? ""}
                                      as="input"
                                      type="text"
                                      readOnly
                                    />
                                  </Col>
                                </Form.Group>
                                <Form.Group as={Row} controlId="frontages_text">
                                  <Form.Label
                                    as={Col}
                                    className="text-right"
                                    column
                                    sm={6}
                                    md={6}
                                    lg={4}
                                  >
                                    Frontages (Text)
                                  </Form.Label>
                                  <Col>
                                    <Form.Control
                                      onChange={handleChange}
                                      value={frontages_text || ""}
                                      as="input"
                                      type="text"
                                      onBlur={handleBlur}
                                      disabled={head_record_property_id ? true : false}
                                    />
                                  </Col>
                                </Form.Group>

                                <Form.Group as={Row} controlId="zoning_text">
                                  <Form.Label
                                    as={Col}
                                    className="text-right"
                                    column
                                    sm={6}
                                    md={6}
                                    lg={4}
                                  >
                                    Zoning
                                  </Form.Label>
                                  <Col>
                                    <Form.Control
                                      onChange={handleChange}
                                      value={zoning_text || ""}
                                      as="textarea"
                                      type="text"
                                      onBlur={handleBlur}
                                      rows="2"
                                      disabled={head_record_property_id ? true : false}
                                    />
                                  </Col>
                                </Form.Group>
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col sm={12} md={6} lg={3}>
                            {true && (
                              <Card>
                                <Card.Header>CoreLogic Equivalent Properties</Card.Header>
                                <Card.Body>
                                  <Card>
                                    <Card.Body>
                                      <Button
                                        id="property-integrations-verify-button"
                                        onClick={togglePropertyIntegrationsModal}
                                        size="sm"
                                        variant="secondary"
                                        type="button"
                                      >
                                        Verify
                                      </Button>
                                      <RefreshButton
                                        id="property-integrations-refresh-button"
                                        onClick={updateIntegrations}
                                        onTimeout={getIntegrations}
                                        timeout={30000}
                                        size="sm"
                                        variant="secondary"
                                        type="button"
                                        style={{ float: "right" }}
                                      />
                                      <PropertyIntegrationsList
                                        filter={addressFilter}
                                        isAuthenticated={isAuthenticated}
                                        controlType="list"
                                        data={integrations}
                                        updateTableState={updateTableState}
                                        saveRecord={saveRecord}
                                        deleteRecord={deleteRecord}
                                        acceptIntegration={acceptIntegration}
                                        rejectIntegration={rejectIntegration}
                                        ctx="cs"
                                      />
                                    </Card.Body>
                                  </Card>
                                </Card.Body>
                              </Card>
                            )}
                            {true && (
                              <Card>
                                <Card.Header>Parcels (Top 5 - click Edit to view all)</Card.Header>
                                <Card.Body>
                                  <Col>
                                    <Button
                                      onClick={toggleTitleDetailsModal}
                                      size="sm"
                                      variant="secondary"
                                      type="button"
                                      disabled={head_record_property_id ? true : false}
                                    >
                                      Edit
                                    </Button>
                                    <TitleDetailsList
                                      filter={addressFilter}
                                      isAuthenticated={isAuthenticated}
                                      controlType="list"
                                      data={titleDetails}
                                      dataFetch={getTitleDetails}
                                      updateTableState={updateTableState}
                                      saveRecord={saveRecord}
                                      deleteRecord={deleteRecord}
                                      stateCode={state}
                                      user={user}
                                      validateRelatedRecord={validateRelatedRecord}
                                    />
                                    <br />
                                  </Col>
                                </Card.Body>
                              </Card>
                            )}
                            {true && (
                              <Card>
                                <Card.Header>
                                  Volume Folios (Top 5 - click Edit to view all)
                                </Card.Header>
                                <Card.Body>
                                  <Col>
                                    <Button
                                      onClick={toggleVolumeFoliosModal}
                                      size="sm"
                                      variant="secondary"
                                      type="button"
                                      disabled={head_record_property_id ? true : false}
                                    >
                                      Edit
                                    </Button>
                                    <br />
                                    <VolumeFoliosList
                                      filter={addressFilter}
                                      isAuthenticated={isAuthenticated}
                                      controlType="list"
                                      data={volumeFolios}
                                      dataFetch={getVolumeFolios}
                                      updateTableState={updateTableState}
                                      saveRecord={saveRecord}
                                      deleteRecord={deleteRecord}
                                      user={user}
                                      validateRelatedRecord={validateRelatedRecord}
                                      stateCode={state}
                                    />
                                  </Col>
                                </Card.Body>
                              </Card>
                            )}
                          </Col>
                          <Col sm={12} md={6} lg={3}>
                            <Card>
                              <Card.Header>Title Details</Card.Header>
                              <Card.Body>
                                <Form.Group as={Row} controlId="title_details">
                                  <Form.Label
                                    as={Col}
                                    className="text-right"
                                    column
                                    sm={3}
                                    md={3}
                                    lg={3}
                                  >
                                    Title Details
                                  </Form.Label>
                                  <Col>
                                    <Form.Control
                                      onChange={handleChange}
                                      value={title_details || ""}
                                      as="textarea"
                                      type="text"
                                      onBlur={handleBlur}
                                      rows="10"
                                      disabled={true}
                                    />
                                  </Col>
                                </Form.Group>
                                <Form.Group as={Row} controlId="title_details_postamble">
                                  <Form.Label
                                    as={Col}
                                    className="text-right"
                                    column
                                    sm={3}
                                    md={3}
                                    lg={3}
                                  >
                                    Title Details Postamble
                                  </Form.Label>
                                  <Col>
                                    <Form.Control
                                      onChange={handleChange}
                                      value={title_details_postamble || ""}
                                      as="textarea"
                                      type="text"
                                      onBlur={handleBlur}
                                      rows="2"
                                      disabled={head_record_property_id ? true : false}
                                    />
                                  </Col>
                                </Form.Group>
                                <Form.Group as={Row} controlId="title_notes">
                                  <Form.Label
                                    as={Col}
                                    className="text-right"
                                    column
                                    sm={3}
                                    md={3}
                                    lg={3}
                                  >
                                    Title Notes
                                  </Form.Label>
                                  <Col>
                                    <Form.Control
                                      onChange={handleChange}
                                      value={title_notes || ""}
                                      as="textarea"
                                      type="text"
                                      onBlur={handleBlur}
                                      rows="2"
                                      disabled={head_record_property_id ? true : false}
                                      bsPrefix="internal"
                                    />
                                  </Col>
                                </Form.Group>
                                <Form.Group as={Row} controlId="archived_title_details">
                                  <Form.Label
                                    as={Col}
                                    className="text-right"
                                    column
                                    sm={3}
                                    md={3}
                                    lg={3}
                                  >
                                    Archived Title Details
                                  </Form.Label>
                                  <Col>
                                    <Form.Control
                                      onChange={handleChange}
                                      value={title_details_archive || ""}
                                      as="textarea"
                                      type="text"
                                      onBlur={handleBlur}
                                      rows="5"
                                      disabled={true}
                                    />
                                  </Col>
                                </Form.Group>
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col sm={12} md={6} lg={3}>
                            <Card>
                              <Card.Header>Photos</Card.Header>
                              <Card.Body>
                                {propertyPhotos.length === 0 && <span>No photos.</span>}
                                {propertyPhotos.map((p, index) => (
                                  <div key={p.url || `photo-${index}`} style={{ marginBottom: "15px" }}>
                                    {p.url ? (
                                      <div
                                        style={{
                                          width: "100%",
                                          maxWidth: `${PROPERTY_PHOTO_WIDTH}px`,
                                          height: `${PROPERTY_PHOTO_HEIGHT}px`,
                                          margin: "0",
                                          overflow: "hidden",
                                          borderRadius: "4px",
                                          backgroundColor: "#f5f5f5",
                                        }}
                                      >
                                        <img
                                          src={p.url}
                                          alt={`Property ${index + 1}`}
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "contain",
                                            objectPosition: "center",
                                            display: "block",
                                          }}
                                          onError={(e) => {
                                            e.target.alt = "Failed to load image";
                                            e.target.style.display = "none";
                                          }}
                                        />
                                      </div>
                                    ) : (
                                      <span style={{ color: "#999" }}>No image URL available</span>
                                    )}
                                    <span className="pl-1" style={{ display: "block", fontSize: "12px", color: "#666" }}>
                                      <strong>Photo Last Modified:</strong>&nbsp;
                                      {new Moment(p.last_edited_date).local().format("DD-MM-YYYY")}
                                    </span>
                                  </div>
                                ))}
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Tab>
                  <Tab eventKey={0} title="Property Details" tabAttrs={getPropertyTabAttrs(0)}>
                    <Card>
                      <Card.Body>
                        <Form.Group as={Form.Row}>
                          <Col>
                            {head_record_property_id && (
                              <div>
                                <font color="red">
                                  This property has a head record in a non-unit scope. Edit the
                                  property details in the non-unit scope.
                                </font>
                              </div>
                            )}
                          </Col>
                        </Form.Group>
                        {showCityscopeReference && (
                          <Row>
                            <Form.Group as={Col} sm={1} md={1} lg={1} controlId="cityscope">
                              <Form.Label className="text-right">Cityscope</Form.Label>

                              <Select
                                value={selectedCityscope}
                                onChange={handleCityscopeChange}
                                options={cityscopeOptions}
                                onInputChange={handleInputChange}
                              />
                            </Form.Group>

                            <Form.Group as={Col} sm={1} md={1} lg={1} controlId="map_number">
                              <Form.Label className="text-right">Map No.</Form.Label>

                              <Form.Control
                                onChange={handleChange}
                                value={map_number ?? ""}
                                as="input"
                                type="text"
                              />
                            </Form.Group>
                            <Form.Group
                              as={Col}
                              sm={1}
                              md={1}
                              lg={1}
                              controlId="map_number_suffix"
                            >
                              <Form.Label className="text-right">Map No. Suffix</Form.Label>

                              <Form.Control
                                onChange={handleChange}
                                value={map_number_suffix ?? ""}
                                as="input"
                                type="text"
                              />
                            </Form.Group>
                            <Form.Group
                              as={Col}
                              sm={1}
                              md={1}
                              lg={1}
                              controlId="property_number"
                            >
                              <Form.Label className="text-right">Property No.</Form.Label>

                              <Form.Control
                                onChange={handleChange}
                                value={property_number ?? ""}
                                as="input"
                                type="text"
                              />
                            </Form.Group>
                            <Form.Group
                              as={Col}
                              sm={1}
                              md={1}
                              lg={1}
                              controlId="property_number_suffix"
                            >
                              <Form.Label className="text-right">Property No. Suffix</Form.Label>

                              <Form.Control
                                onChange={handleChange}
                                value={property_number_suffix ?? ""}
                                as="input"
                                type="text"
                              />
                            </Form.Group>

                            <Form.Group
                              as={Col}
                              sm={1}
                              md={1}
                              lg={1}
                              controlId="unit_number"
                            >
                              <Form.Label className="text-right">Unit No.</Form.Label>

                              <Form.Control
                                onChange={handleChange}
                                value={unit_number ?? ""}
                                as="input"
                                type="number"
                              />
                            </Form.Group>

                            <Form.Group
                              as={Col}
                              sm={1}
                              md={1}
                              lg={1}
                              controlId="unit_number_suffix"
                            >
                              <Form.Label className="text-right">Unit No. Suffix </Form.Label>

                              <Form.Control
                                onChange={handleChange}
                                value={unit_number_suffix ?? ""}
                                as="input"
                                type="text"
                              />
                            </Form.Group>

                            <Form.Group as={Col} sm={1} md={1} lg={1} controlId="unit_sort">
                              <Form.Label className="text-right">Unit Sort</Form.Label>

                              <Form.Control
                                onChange={handleChange}
                                value={unit_sort ?? ""}
                                as="input"
                                type="text"
                              />
                            </Form.Group>
                            <Col>
                              <Button variant="primary" onClick={handleRenumber} type="button">
                                Save
                              </Button>{" "}
                              <Button
                                variant="primary"
                                type="button"
                                onClick={cancelCityscopeReferenceChange}
                              >
                                Cancel
                              </Button>{" "}
                            </Col>
                          </Row>
                        )}

                        {!showCityscopeReference && (
                          <Row>
                            <Col sm={12} md={6} lg={3}>
                              <Card>
                                <Card.Header>Description</Card.Header>
                                {/* Description */}
                                <Card.Body>
                                  <div style={{ marginBottom: "0.75rem" }}>
                                    <Button
                                      size="sm"
                                      type="button"
                                      variant="secondary"
                                      onClick={toggleCityscopeReference}
                                    >
                                      Renumber Property
                                    </Button>{" "}
                                    <Button
                                      size="sm"
                                      variant="secondary"
                                      type="button"
                                      onClick={handleDelete}
                                    >
                                      Delete Property
                                    </Button>
                                  </div>
                                  <Form.Group as={Row} controlId="description">
                                    {/* <Form.Label className="text-right">Description</Form.Label> */}

                                    <Form.Control
                                      onChange={handleChange}
                                      value={description || ""}
                                      as="textarea"
                                      rows="25"
                                      onBlur={handleBlur}
                                      disabled={head_record_property_id ? true : false}
                                      style={{ minHeight: "calc(25 * 1.5em + 0.75rem + 2px)" }}
                                    />
                                  </Form.Group>

                                  <Form.Group as={Row} controlId="description_postamble">
                                    <Form.Label className="text-right">
                                      Description Postamble
                                    </Form.Label>

                                    <Form.Control
                                      onChange={handleChange}
                                      value={description_postamble || ""}
                                      as="textarea"
                                      rows="2"
                                      onBlur={handleBlur}
                                      style={{ minHeight: "calc(2 * 1.5em + 0.75rem + 2px)" }}
                                    />
                                  </Form.Group>
                                  <Form.Group as={Row} controlId="description_notes">
                                    <Form.Label className="text-right">
                                      Description Notes
                                    </Form.Label>

                                    <Form.Control
                                      onChange={handleChange}
                                      value={description_notes || ""}
                                      as="textarea"
                                      rows="6"
                                      onBlur={handleBlur}
                                      disabled={head_record_property_id ? true : false}
                                      bsPrefix="internal"
                                      style={{ minHeight: "calc(6 * 1.5em + 0.75rem + 2px)" }}
                                    />
                                  </Form.Group>
                                </Card.Body>
                              </Card>
                            </Col>
                            <Col sm={12} md={6} lg={3}>
                              <Card>
                                <Card.Header>Key Attributes</Card.Header>
                                <Card.Body>
                                  <Form.Group as={Row} controlId="sub_divided_unit">
                                    <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                      Sub-divided Unit
                                    </Form.Label>
                                    <Col>
                                      <Form.Check
                                        checked={sub_divided_unit}
                                        onChange={handleCheckBoxChange}
                                        label=""
                                        type="checkbox"
                                        name="sub_divided_unit"
                                        disabled={disableSubDividedUnit}
                                      />
                                    </Col>
                                  </Form.Group>

                                  <Form.Group as={Row} controlId="year_built">
                                    <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                      Year Built
                                    </Form.Label>
                                    <Col>
                                      <Form.Control
                                        onChange={handleChange}
                                        value={year_built ?? ""}
                                        as="input"
                                        type="number"
                                        onBlur={handleBlur}
                                        disabled={head_record_property_id ? true : false}
                                      />
                                    </Col>
                                  </Form.Group>

                                  <Form.Group as={Row} controlId="year_refurb">
                                    <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                      Year Refurbished
                                    </Form.Label>
                                    <Col>
                                      <Form.Control
                                        onChange={handleChange}
                                        value={year_refurb ?? ""}
                                        as="input"
                                        type="number"
                                        onBlur={handleBlur}
                                        disabled={head_record_property_id ? true : false}
                                      />
                                    </Col>
                                  </Form.Group>

                                  <Form.Group as={Row} controlId="bedrooms">
                                    <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                      Bedrooms
                                    </Form.Label>
                                    <Col>
                                      <Form.Control
                                        onChange={handleChange}
                                        value={bedrooms ?? ""}
                                        as="input"
                                        type="number"
                                        onBlur={handleBlur}
                                        disabled={head_record_property_id ? true : false}
                                      />
                                    </Col>
                                  </Form.Group>
                                  <Form.Group as={Row} controlId="storeys">
                                    <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                      Storeys{" "}
                                    </Form.Label>
                                    <Col>
                                      <Form.Control
                                        onChange={handleChange}
                                        value={storeys ?? ""}
                                        as="input"
                                        type="number"
                                        onBlur={handleBlur}
                                        disabled={head_record_property_id ? true : false}
                                      />
                                    </Col>
                                  </Form.Group>
                                  <Form.Group as={Row} controlId="parking_yes_no">
                                    <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                      Parking
                                    </Form.Label>
                                    <Col>
                                      <Form.Check
                                        checked={parking_yes_no}
                                        onChange={handleCheckBoxChange}
                                        name="parking_yes_no"
                                        type="checkbox"
                                        label=""
                                        disabled={head_record_property_id ? true : false}
                                      />
                                    </Col>
                                  </Form.Group>
                                  <Form.Group as={Row} controlId="parking_num">
                                    <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                      Parking No.
                                    </Form.Label>
                                    <Col>
                                      <Form.Control
                                        onChange={handleChange}
                                        value={parking_num ?? ""}
                                        as="input"
                                        type="number"
                                        onBlur={handleBlur}
                                        disabled={head_record_property_id ? true : false}
                                      />
                                    </Col>
                                  </Form.Group>

                                  <Form.Group as={Row} controlId="typical_floor_area">
                                    <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                      Typical Floor Area
                                    </Form.Label>
                                    <Col>
                                      <InputGroup className="mb-3">
                                        <Form.Control
                                          onChange={handleChange}
                                          value={typical_floor_area ?? ""}
                                          as="input"
                                          type="number"
                                          onBlur={handleBlur}
                                          disabled={
                                            !disableSubDividedUnit || head_record_property_id
                                              ? true
                                              : false
                                          }
                                        />
                                        <InputGroup.Text>m2</InputGroup.Text>
                                      </InputGroup>
                                    </Col>
                                  </Form.Group>

                                  <Form.Group as={Row} controlId="building_area">
                                    <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                      Building Area
                                    </Form.Label>
                                    <Col>
                                      <InputGroup>
                                        <Form.Control
                                          onChange={handleChange}
                                          value={building_area ?? ""}
                                          as="input"
                                          type="number"
                                          onBlur={handleBlur}
                                        />
                                        <InputGroup.Text>m2</InputGroup.Text>
                                      </InputGroup>
                                    </Col>
                                  </Form.Group>
                                  <Form.Group as={Row} controlId="nla_date">
                                    <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                      Building Area Source Date
                                    </Form.Label>
                                    <Col>
                                      <div className="customDatePickerWidth">
                                        <DatePicker
                                          selected={nla_date != null ? new Date(nla_date) : ""}
                                          onChange={(e) => handleDateChange(e, "nla_date")}
                                          dateFormat="dd/MM/yyyy"
                                          onBlur={handleBlur}
                                          className="form-control internal"
                                          disabled={head_record_property_id ? true : false}
                                        />
                                      </div>
                                    </Col>
                                  </Form.Group>

                                  <Form.Group as={Row} controlId="site_area">
                                    <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                      Site Area
                                    </Form.Label>
                                    <Col>
                                      <InputGroup>
                                        <Form.Control
                                          onChange={handleChange}
                                          value={site_area ?? ""}
                                          as="input"
                                          type="number"
                                          onBlur={handleBlur}
                                          disabled={head_record_property_id ? true : false}
                                        />
                                        <InputGroup.Text>m2</InputGroup.Text>
                                      </InputGroup>
                                    </Col>
                                  </Form.Group>
                                  <Form.Group as={Row} controlId="unit_area">
                                    <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                      Unit Area
                                    </Form.Label>
                                    <Col>
                                      <InputGroup>
                                        <Form.Control
                                          onChange={handleChange}
                                          value={unit_area ?? ""}
                                          as="input"
                                          type="number"
                                          onBlur={handleBlur}
                                        />
                                        <InputGroup.Text>m2</InputGroup.Text>
                                      </InputGroup>
                                    </Col>
                                  </Form.Group>
                                  <Form.Group as={Row} controlId="unit_level_number">
                                    <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                      Unit Level
                                    </Form.Label>
                                    <Col>
                                      <Form.Control
                                        onChange={handleChange}
                                        value={unit_level_number ?? ""}
                                        as="input"
                                        type="number"
                                        onBlur={handleBlur}
                                        disabled={disableSubDividedUnit}
                                        step="0.5"
                                      />
                                    </Col>
                                  </Form.Group>
                                </Card.Body>
                              </Card>
                            </Col>

                            <Col sm={12} md={6} lg={3}>
                              <Row>
                                <Col>
                                  {!showCityscopeReference && (
                                    <Card>
                                      <Card.Header>Buildings</Card.Header>
                                      <Card.Body>
                                        <Button
                                          onClick={toggleBuildingNameModal}
                                          size="sm"
                                          variant="secondary"
                                          type="button"
                                          disabled={head_record_property_id ? true : false}
                                        >
                                          Edit
                                        </Button>
                                        <BuildingNameList
                                          filter={buildingNameFilter}
                                          user={user}
                                          isAuthenticated={isAuthenticated}
                                          controlType="list"
                                          data={buildingNames}
                                          updateTableState={updateTableState}
                                          saveRecord={saveRecord}
                                          deleteRecord={deleteRecord}
                                        />
                                      </Card.Body>
                                    </Card>
                                  )}
                                  {!showCityscopeReference && (
                                    <Card>
                                      <Card.Header>Classification</Card.Header>
                                      <Card.Body>
                                        <Button
                                          onClick={togglePropertyClassificationModal}
                                          size="sm"
                                          variant="secondary"
                                          type="button"
                                          disabled={head_record_property_id ? true : false}
                                        >
                                          Edit
                                        </Button>
                                        <PropertyClassificationList
                                          filter={propertyClassificationFilter}
                                          user={user}
                                          isAuthenticated={isAuthenticated}
                                          controlType="list"
                                          data={propertyClassifications}
                                          updateTableState={updateTableState}
                                          saveRecord={saveRecord}
                                          deleteRecord={deleteRecord}
                                        />
                                      </Card.Body>
                                    </Card>
                                  )}
                                  {!showCityscopeReference && (
                                    <Card>
                                      <Card.Header>Grade</Card.Header>
                                      <Card.Body>
                                        <Button
                                          onClick={toggleBuildingGradeModal}
                                          size="sm"
                                          variant="secondary"
                                          type="button"
                                          disabled={head_record_property_id ? true : false}
                                        >
                                          Edit
                                        </Button>
                                        <BuildingGradeList
                                          filter={buildingGradeFilter}
                                          user={user}
                                          isAuthenticated={isAuthenticated}
                                          controlType="list"
                                          data={buildingGrades}
                                          updateTableState={updateTableState}
                                          saveRecord={saveRecord}
                                          deleteRecord={deleteRecord}
                                        />
                                      </Card.Body>
                                    </Card>
                                  )}
                                </Col>
                              </Row>
                            </Col>

                            <Col sm={12} md={6} lg={3}>
                              <Card>
                                <Card.Header>Building Contacts</Card.Header>
                                <Card.Body>
                                  <Form.Group as={Row} controlId="internet">
                                    <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                      Website
                                    </Form.Label>
                                    <Col>
                                      <InputGroup>
                                        <InputGroup.Text>www.</InputGroup.Text>
                                        <Form.Control
                                          onChange={handleChange}
                                          value={internet ?? ""}
                                          as="input"
                                          type="text"
                                          onBlur={handleBlur}
                                          pattern="www[.].*[.].*"
                                          placeholder="www.website.com"
                                          disabled={head_record_property_id ? true : false}
                                        />
                                      </InputGroup>
                                    </Col>
                                  </Form.Group>

                                  <Form.Group as={Row} controlId="email">
                                    <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                      Email
                                    </Form.Label>
                                    <Col>
                                      <InputGroup>
                                        <InputGroup.Text>@</InputGroup.Text>
                                        <Form.Control
                                          onChange={handleChange}
                                          value={email ?? ""}
                                          as="input"
                                          type="email"
                                          onBlur={handleBlur}
                                          disabled={head_record_property_id ? true : false}
                                        />
                                      </InputGroup>
                                    </Col>
                                  </Form.Group>

                                  <Form.Group as={Row} controlId="agentSearchString">
                                    <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                      Agency Search
                                    </Form.Label>
                                    <Col>
                                      <InputGroup size="sm">
                                        <Form.Control
                                          onChange={handleChange}
                                          value={agentSearchString}
                                          onKeyDown={handleEnterOnAgentSearch}
                                          as="input"
                                          type="text"
                                          placeholder="Search entities"
                                          disabled={head_record_property_id ? true : false}
                                        />
                                        <Button
                                          onClick={handleAgentSearch}
                                          variant="outline-secondary"
                                          size="sm"
                                          type="button"
                                        >
                                          Search
                                        </Button>
                                      </InputGroup>
                                    </Col>
                                  </Form.Group>
                                  <Form.Group as={Row} controlId="Interstate">
                                    <Col sm={6} md={6} lg={4}></Col>
                                    <Col>
                                      <Form.Check
                                        checked={agentInterstateFlag}
                                        onChange={handleCheckBoxChange}
                                        label="Search interstate"
                                        type="checkbox"
                                        name="agentInterstateFlag"
                                        disabled={head_record_property_id ? true : false}
                                      />
                                    </Col>
                                  </Form.Group>
                                  <Form.Group as={Row} controlId="selectedAgent">
                                    <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                      Managing Agent
                                    </Form.Label>
                                    <Col>
                                      {showAgentDropDown && (
                                        <div>
                                          <Select
                                            isClearable
                                            value={selectedAgent}
                                            onChange={handleAgentChange}
                                            options={agentOptions}
                                            onInputChange={handleInputChange}
                                            onBlur={handleBlur}
                                            menuIsOpen={agentMenuIsOpen}
                                            isDisabled={head_record_property_id ? true : false}
                                          />
                                          <Form.Control
                                            disabled
                                            value={managing_agent_email ?? ""}
                                            readOnly
                                            as="input"
                                            type="text"
                                          />
                                        </div>
                                      )}
                                    </Col>
                                  </Form.Group>

                                  <Form.Group as={Row} controlId="selectedAgentContact">
                                    <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                      Managing Agent Contact
                                    </Form.Label>
                                    <Col>
                                      {showAgentContactDropDown && (
                                        <Select
                                          isClearable
                                          value={selectedAgentContact}
                                          onChange={handleAgentContactChange}
                                          options={agentContactOptions}
                                          onInputChange={handleInputChange}
                                          onBlur={handleBlur}
                                          isDisabled={head_record_property_id ? true : false}
                                        />
                                      )}
                                    </Col>
                                  </Form.Group>
                                  <Form.Group as={Form.Row}>
                                    <Col sm={6} md={6} lg={4}></Col>
                                    <Col>
                                      <LinkContainer
                                        to={{
                                          pathname: "../entities/new",
                                          search: `?cityscopereference=${property_key}`,
                                          state: { propertyidlist: idList },
                                        }}
                                      >
                                        <Button variant="info" size="sm" type="button">
                                          Add New Agent
                                        </Button>
                                      </LinkContainer>{" "}
                                      {entity_id_managing_agent && (
                                        <LinkContainer
                                          to={{
                                            pathname: `../entities/${entity_id_managing_agent}`,
                                            search: `?cityscopereference=${property_key}`,
                                          }}
                                        >
                                          <Button size="sm" variant="info" type="button">
                                            Edit Existing Agent
                                          </Button>
                                        </LinkContainer>
                                      )}
                                    </Col>
                                  </Form.Group>
                                </Card.Body>
                              </Card>
                            </Col>
                          </Row>
                        )}
                      </Card.Body>
                    </Card>
                  </Tab>
                  {strataUnits.length > 0 && (
                    <Tab eventKey={12} title="Strata Units" tabAttrs={getPropertyTabAttrs(12)}>
                      <Form.Group as={Form.Row}>
                        <Col>
                          <Nav className="justify-content-left">
                            <Nav.Item>
                              <div>
                                <Button
                                  variant="primary"
                                  onClick={handleCopyBuildingNamesToStrata}
                                  type="button"
                                  disabled={buildingNames.length === 0}
                                >
                                  Copy Building Names
                                </Button>{" "}
                              </div>
                            </Nav.Item>

                            <Nav.Item>
                              <div>
                                <Button
                                  variant="primary"
                                  onClick={handleCopyClassificationsToStrata}
                                  type="button"
                                  disabled={propertyClassifications.length === 0}
                                >
                                  Copy Classifications
                                </Button>{" "}
                              </div>
                            </Nav.Item>
                            <Nav.Item>
                              <div>
                                <Button
                                  variant="primary"
                                  onClick={handleCopyAddressesToStrata}
                                  type="button"
                                  disabled={addresses.length === 0}
                                >
                                  Copy Street Address
                                </Button>{" "}
                              </div>
                            </Nav.Item>
                            <Nav.Item>
                              <div>
                                <Button
                                  variant="primary"
                                  onClick={handleCopyPropertySuburb}
                                  type="button"
                                >
                                  Copy Suburb+State+PC
                                </Button>{" "}
                              </div>
                            </Nav.Item>
                            <Nav.Item>
                              <div>
                                <Button
                                  variant="primary"
                                  onClick={handleCopyOwnersToStrata}
                                  type="button"
                                  disabled={propertyOwners.length === 0}
                                >
                                  Copy Owners
                                </Button>{" "}
                              </div>
                            </Nav.Item>
                            <Nav.Item>
                              <div>
                                <Button
                                  variant="primary"
                                  onClick={handleCopyParcelsToStrata}
                                  type="button"
                                  disabled={titleDetails.length === 0}
                                >
                                  Copy Parcels
                                </Button>{" "}
                              </div>
                            </Nav.Item>
                          </Nav>
                        </Col>
                      </Form.Group>
                      <Form.Group as={Form.Row}>
                        <Col>
                          <Card>
                            {/* <Card.Header >

                                                    Strata Units

                                                </Card.Header> */}

                            <Card.Body>
                              <Card>
                                <Card.Body>
                                  <StrataUnitList
                                    user={user}
                                    isAuthenticated={isAuthenticated}
                                    data={strataUnits}
                                    updateTableState={updateTableState}
                                    saveRecord={saveRecord}
                                    deleteRecord={deleteRecord}
                                    validateRelatedRecord={validateRelatedRecord}
                                    controlType="table"
                                    propertyid={property_id}
                                    propertykey={property_key}
                                  />
                                </Card.Body>
                              </Card>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Form.Group>
                    </Tab>
                  )}

                  {/* {!showCityscopeReference &&*/}

                  <Tab eventKey={1} title={"Tenants"} tabAttrs={getPropertyTabAttrs(1)}>
                    <Card>
                      {/* <Card.Header >

                                                     Tenants

                                                </Card.Header> */}

                      <Card.Body>
                        <Row style={{ marginBottom: "0.75rem" }}>
                          <Form.Group as={Col} controlId="occupants_preamble">
                            <Form.Label>Occupants Preamble</Form.Label>

                            <Form.Control
                              onChange={handleChange}
                              value={occupants_preamble || ""}
                              as="textarea"
                              type="text"
                              onBlur={handleBlur}
                              rows="2"
                              disabled={head_record_property_id ? true : false}
                            />
                          </Form.Group>

                          <Form.Group as={Col} controlId="occupants_postamble">
                            <Form.Label>Occupants Postamble</Form.Label>

                            <Form.Control
                              onChange={handleChange}
                              value={occupants_postamble || ""}
                              as="textarea"
                              type="text"
                              onBlur={handleBlur}
                              rows="2"
                              disabled={head_record_property_id ? true : false}
                            />
                          </Form.Group>

                          <Form.Group as={Col} controlId="occupants_notes">
                            <Form.Label>Occupants Notes</Form.Label>

                            <Form.Control
                              onChange={handleChange}
                              value={occupants_notes || ""}
                              as="textarea"
                              type="text"
                              onBlur={handleBlur}
                              rows="2"
                              disabled={head_record_property_id ? true : false}
                              bsPrefix="internal"
                            />
                          </Form.Group>
                        </Row>

                        <Card>
                          <Card.Body>
                            <LeaseList
                              isAuthenticated={isAuthenticated}
                              user={user}
                              data={leases}
                              updateTableState={updateTableState}
                              saveLeaseRecord={saveLeaseRecord}
                              deleteRecord={deleteRecord}
                              validateRelatedRecord={validateRelatedRecord}
                              floorTypeOptions={floorTypeOptions}
                              leaseClassificationOptions={lookupLeaseClassificationOptions}
                              propertyid={property_id}
                              propertykey={property_key}
                              buildingNameOptions={buildingNames}
                              controlType="table"
                              idList={idList}
                            />
                          </Card.Body>
                        </Card>
                      </Card.Body>
                    </Card>
                  </Tab>
                  <Tab eventKey={2} title="For Lease" tabAttrs={getPropertyTabAttrs(2)}>
                    <Card>
                      {/* <Card.Header >

                                                     For Lease ({leasingAgents.length})

                                                </Card.Header> */}

                      <Card.Body>
                        <Card>
                          <Card.Body>
                            <LeasingAgentList
                              isAuthenticated={isAuthenticated}
                              user={user}
                              data={leasingAgents}
                              updateTableState={updateLeasingAgentsTableState}
                              saveRecord={saveLeasingAgentRecord}
                              state={state}
                              validateRelatedRecord={validateRelatedRecord}
                              getAgentsLeasing={getAgentsLeasing}
                              getLeasingAgentContacts={getLeasingAgentContacts}
                              leasingAgentOptions={leasingAgentOptions}
                              leasingAgentContactOptions={leasingAgentContactOptions}
                              propertyid={property_id}
                              controlType="propertyDetailForLeaseTable"
                            />
                          </Card.Body>
                        </Card>
                        <Card>
                          <Card.Body>
                            <Form.Group as={Form.Row} className="show-grid with-margin">
                              <Col>
                                <Form.Group as={Row} controlId="leasing_notes">
                                  <Form.Label column sm={6} md={6} lg={4}>
                                    Leasing Notes.
                                  </Form.Label>
                                  <Form.Control
                                    onChange={handleChange}
                                    value={leasing_notes || ""}
                                    as="textarea"
                                    type="text"
                                    rows="3"
                                    onBlur={handleBlur}
                                    bsPrefix="internal"
                                  />
                                </Form.Group>
                              </Col>
                            </Form.Group>
                          </Card.Body>
                        </Card>
                      </Card.Body>
                    </Card>
                  </Tab>
                  <Tab eventKey={3} title="Occ. Rates" tabAttrs={getPropertyTabAttrs(3)}>
                    <Card>
                      <Card.Body>
                        <OccupancyRateList
                          isAuthenticated={isAuthenticated}
                          user={user}
                          data={occupancyRates}
                          updateTableState={updateTableState}
                          saveRecord={saveRecord}
                          deleteRecord={deleteRecord}
                          filter={occupancyFilter}
                          validateRelatedRecord={validateRelatedRecord}
                          controlType="table"
                          propertyid={property_id}
                        />
                      </Card.Body>
                    </Card>
                  </Tab>
                  <Tab eventKey={4} title="Proposals" tabAttrs={getPropertyTabAttrs(4)}>
                    <Card>
                      {/* <Card.Header >

                                                     Proposals ({propertyProposals.length})

                                                </Card.Header> */}

                      <Card.Body>
                        <Card>
                          <Card.Body>
                            <Form.Group as={Form.Row}>
                              <Col>
                                <PropertyProposalList
                                  isAuthenticated={isAuthenticated}
                                  user={user}
                                  data={propertyProposals}
                                  updateTableState={updateTableState}
                                  saveRecord={saveRecord}
                                  deleteRecord={deleteRecord}
                                  validateRelatedRecord={validateRelatedRecord}
                                  controlType="table"
                                  propertyid={property_id}
                                  propertykey={property_key}
                                  idList={idList}
                                />
                              </Col>
                            </Form.Group>
                          </Card.Body>
                        </Card>
                      </Card.Body>
                    </Card>
                  </Tab>
                  <Tab eventKey={5} title="Nabers" tabAttrs={getPropertyTabAttrs(5)}>
                    <Card>
                      {/* <Card.Header >

                                                    Nabers ({nabers.length})

                                                </Card.Header> */}

                      <Card.Body>
                        <Card>
                          <Card.Body>
                            <NaberList
                              isAuthenticated={isAuthenticated}
                              user={user}
                              data={nabers}
                              updateTableState={updateTableState}
                              saveRecord={saveRecord}
                              deleteRecord={deleteRecord}
                              nabersRatingTypeOptions={nabersRatingTypeOptions}
                              filter={naberFilter}
                              validateRelatedRecord={validateRelatedRecord}
                              nabersRatingValues={nabersRatingValues}
                              controlType="table"
                              propertyid={property_id}
                            />
                          </Card.Body>
                        </Card>
                      </Card.Body>
                    </Card>
                  </Tab>
                  <Tab eventKey={6} title="Lifts" tabAttrs={getPropertyTabAttrs(6)}>
                    <Card>
                      {/* <Card.Header >

                                                    Lift Operators ({liftOperators.length})

                                                </Card.Header>
                                                 */}
                      <Card.Body>
                        <Card>
                          <Card.Body>
                            <LiftOperatorList
                              isAuthenticated={isAuthenticated}
                              user={user}
                              data={liftOperators}
                              updateTableState={updateLiftOperatorsTableState}
                              saveRecord={saveLiftOperatorRecord}
                              controlType="propertyDetailLiftsTable"
                              liftOperatorOptions={lookupLiftOperatorOptions}
                              filter={liftOperatorFilter}
                              validateRelatedRecord={validateRelatedRecord}
                              propertyid={property_id}
                            />
                            <Form.Group as={Row} controlId="lift_notes">
                              <Form.Label column sm={3} md={2} lg={2}>
                                Lift Notes
                              </Form.Label>
                              <Col>
                                <Form.Control
                                  onChange={handleChange}
                                  value={lift_notes || ""}
                                  as="textarea"
                                  rows="7"
                                  onBlur={handleBlur}
                                  disabled={head_record_property_id ? true : false}
                                  bsPrefix="internal"
                                />
                              </Col>
                            </Form.Group>
                          </Card.Body>
                        </Card>
                      </Card.Body>
                    </Card>
                  </Tab>
                  <Tab eventKey={7} title="Hist. Listings" tabAttrs={getPropertyTabAttrs(7)}>
                    <Card>
                      <Card.Header>Historical Listings</Card.Header>

                      <Card.Body>
                        <Card>
                          <Card.Body>
                            <Form.Group as={Row} controlId="historical_listings">
                              <Form.Label column sm={6} md={6} lg={4}>
                                Historical Listings
                              </Form.Label>
                              <Col>
                                <Form.Control
                                  onChange={handleChange}
                                  value={historical_listings || ""}
                                  as="textarea"
                                  type="text"
                                  rows="2"
                                  onBlur={handleBlur}
                                  disabled={head_record_property_id ? true : false}
                                />
                              </Col>
                            </Form.Group>
                            <Form.Group as={Row} controlId="historical_notes">
                              <Form.Label column sm={6} md={6} lg={4}>
                                Historical Listing Notes
                              </Form.Label>
                              <Col>
                                <Form.Control
                                  onChange={handleChange}
                                  value={historical_notes || ""}
                                  as="textarea"
                                  rows="2"
                                  onBlur={handleBlur}
                                  disabled={head_record_property_id ? true : false}
                                  bsPrefix="internal"
                                />
                              </Col>
                            </Form.Group>
                          </Card.Body>
                        </Card>
                      </Card.Body>
                    </Card>
                  </Tab>
                  <Tab eventKey={8} title="Sales" tabAttrs={getPropertyTabAttrs(8)}>
                    <Card>
                      {/* <Card.Header >Sales</Card.Header> */}

                      <Card.Body>
                        <Row>
                          <Col sm={6} md={6} lg={8}>
                            <Form.Group as={Row} controlId="title_details">
                              <Form.Label column sm={6} md={3} lg={2}>
                                Title Details
                              </Form.Label>
                              <Col>
                                <Row>
                                  <Col>
                                    <Form.Control
                                      onChange={handleChange}
                                      value={title_details || ""}
                                      as="textarea"
                                      type="text"
                                      onBlur={handleBlur}
                                      rows="2"
                                      disabled={true}
                                    />
                                  </Col>
                                </Row>
                              </Col>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row>
                          <Col sm={6} md={6} lg={8}>
                            <Form.Group as={Row} controlId="sales_text">
                              <Form.Label column sm={6} md={3} lg={2}>
                                Sales History
                              </Form.Label>
                              <Col>
                                <Form.Control
                                  onChange={handleChange}
                                  value={sales_text || ""}
                                  as="textarea"
                                  type="text"
                                  onBlur={handleBlur}
                                  rows="5"
                                  disabled={head_record_property_id ? true : false}
                                  style={{ minHeight: "calc(5 * 1.5em + 0.75rem + 2px)" }}
                                />
                              </Col>
                            </Form.Group>

                            <Form.Group as={Row} controlId="sales_history_postamble">
                              <Form.Label column sm={6} md={3} lg={2}>
                                Sales History Postamble
                              </Form.Label>
                              <Col>
                                <Form.Control
                                  onChange={handleChange}
                                  value={sales_history_postamble || ""}
                                  as="textarea"
                                  type="text"
                                  onBlur={handleBlur}
                                  rows="1"
                                  style={{ minHeight: "calc(1 * 1.5em + 0.75rem + 2px)" }}
                                />
                              </Col>
                            </Form.Group>
                          </Col>

                          <Col>
                            <Form.Group as={Row} controlId="sales_notes">
                              <Form.Label column sm={6} md={3} lg={2}>
                                Sales Notes
                              </Form.Label>
                              <Col>
                                <Form.Control
                                  onChange={handleChange}
                                  value={sales_notes || ""}
                                  as="textarea"
                                  type="text"
                                  onBlur={handleBlur}
                                  rows="5"
                                  disabled={head_record_property_id ? true : false}
                                  bsPrefix="internal"
                                  style={{ minHeight: "calc(5 * 1.5em + 0.75rem + 2px)" }}
                                />
                              </Col>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Card>
                          <Card.Body>
                            <PropertySaleList
                              isAuthenticated={isAuthenticated}
                              user={user}
                              data={propertySales}
                              isLoading={isLoading}
                              updateTableState={updateTableState}
                              controlType="table"
                              saveRecord={(record, dataSet, id) => saveRecord(dataSet, record)}
                              deleteRecord={deleteRecord}
                              idList={idList}
                              validateRelatedRecord={validateRelatedRecord}
                              propertyid={property_id}
                              propertykey={property_key}
                              primaryCategory={
                                propertyClassifications.find((classification) => Number(classification.sort_order) === 1)
                                  || propertyClassifications[0]
                                  || null
                              }
                              IsUnit={
                                unit_number &&
                                (parking_yes_no || parking_num) &&
                                propertyClassifications
                                  .map((r) => r.classification)
                                  .indexOf("Car Park") < 0
                                  ? true
                                  : false
                              }
                            />
                          </Card.Body>
                        </Card>
                      </Card.Body>
                    </Card>
                  </Tab>
                  <Tab eventKey={9} title="Ownership" tabAttrs={getPropertyTabAttrs(9)}>
                    <Card>
                      {/* <Card.Header >Ownership ({propertyOwners.length})</Card.Header> */}

                      <Card.Body>
                        <Form.Group as={Row} controlId="owner_preamble">
                          <Form.Label column sm={3} md={3} lg={1}>
                            Owner Preamble
                          </Form.Label>
                          <Col>
                            <Form.Control
                              onChange={handleChange}
                              value={owner_preamble || ""}
                              as="textarea"
                              type="text"
                              onBlur={handleBlur}
                              rows="3"
                              style={{ minHeight: "calc(3 * 1.5em + 0.75rem + 2px)" }}
                              disabled={head_record_property_id ? true : false}
                            />
                          </Col>
                        </Form.Group>

                        <Card>
                          <Card.Body>
                            <Form.Group as={Form.Row}>
                              <Col className="text-right">
                                <Button
                                  variant="info"
                                  onClick={toggleOwnerSearchDialog}
                                  type="button"
                                  size="sm"
                                >
                                  Search + Add Owners
                                </Button>{" "}
                                <Form.Label className="mb-0 ml-2 mr-1 align-middle">
                                  Copy Owners from:
                                </Form.Label>
                                <span style={{ display: "inline-block", verticalAlign: "top" }} className="mx-1">
                                  <Form.Control
                                    onChange={handleChange}
                                    id="copyPropertyOwner"
                                    value={copyPropertyOwner ?? ""}
                                    as="input"
                                    type="text"
                                    onBlur={handleBlur}
                                    placeholder="Cityscope ref"
                                    style={{ width: "auto" }}
                                  />
                                  <Form.Check
                                    checked={copyPropertyOwnerAndDeleteFlag}
                                    onChange={handleCheckBoxChange}
                                    name="copyPropertyOwnerAndDeleteFlag"
                                    label="Delete existing?"
                                    type="checkbox"
                                  />
                                </span>
                                <Button
                                  variant="info"
                                  onClick={handleCopyPropertyOwners}
                                  type="button"
                                  size="sm"
                                >
                                  Copy
                                </Button>
                              </Col>
                            </Form.Group>
                            <Form.Group as={Form.Row}>
                              <Col>
                                <PropertyOwnerList
                                  isAuthenticated={isAuthenticated}
                                  user={user}
                                  data={propertyOwners}
                                  updateTableState={updateTableState}
                                  saveRecord={saveRecord}
                                  deleteRecord={deleteRecord}
                                  idList={idList}
                                  controlType="table"
                                  validateRelatedRecord={validateRelatedRecord}
                                  propertyid={property_id}
                                  propertykey={property_key}
                                />
                              </Col>
                            </Form.Group>
                          </Card.Body>
                        </Card>

                        <Form.Group as={Row} controlId="owner_postamble">
                          <Form.Label column sm={3} md={3} lg={1}>
                            Owner Postamble
                          </Form.Label>
                          <Col>
                            <Form.Control
                              onChange={handleChange}
                              value={owner_postamble || ""}
                              as="textarea"
                              type="text"
                              onBlur={handleBlur}
                              rows="3"
                              style={{ minHeight: "calc(3 * 1.5em + 0.75rem + 2px)" }}
                              disabled={head_record_property_id ? true : false}
                            />
                          </Col>
                        </Form.Group>
                        <Form.Group as={Row} controlId="lease_term">
                          <Form.Label column sm={3} md={3} lg={1}>
                            Lease Term
                          </Form.Label>
                          <Col>
                            <Form.Control
                              onChange={handleChange}
                              value={lease_term || ""}
                              as="textarea"
                              type="text"
                              onBlur={handleBlur}
                              rows="3"
                              style={{ minHeight: "calc(3 * 1.5em + 0.75rem + 2px)" }}
                              disabled={head_record_property_id ? true : false}
                            />
                          </Col>
                        </Form.Group>
                        <Form.Group as={Row} controlId="owner_notes">
                          <Form.Label column sm={6} md={6} lg={1}>
                            Owner Notes
                          </Form.Label>
                          <Col>
                            <Form.Control
                              onChange={handleChange}
                              value={owner_notes || ""}
                              as="textarea"
                              type="text"
                              rows="3"
                              style={{ minHeight: "calc(3 * 1.5em + 0.75rem + 2px)" }}
                              onBlur={handleBlur}
                              disabled={head_record_property_id ? true : false}
                              bsPrefix="internal"
                            />
                          </Col>
                        </Form.Group>
                      </Card.Body>
                    </Card>
                  </Tab>
                  <Tab eventKey={10} title={"Queries (" + activeQueries.length + ")"} tabAttrs={getPropertyTabAttrs(10)}>
                    <Card>
                      {/* <Card.Header >Queries ({activeQueries.length})</Card.Header> */}
                      <Card.Body>
                        <Form.Group as={Form.Row}>
                          <Col sm={6} md={6} lg={4}>
                            <Form.Group as={Row} controlId="photo_required">
                              <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                Photo Required
                              </Form.Label>
                              <Col>
                                <Form.Check
                                  checked={photo_required}
                                  onChange={handleCheckBoxChange}
                                  name="photo_required"
                                  label=""
                                  type="checkbox"
                                />
                              </Col>
                            </Form.Group>

                            <Form.Group as={Row} controlId="photo_why">
                              <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                New Photo Reason
                              </Form.Label>
                              <Col>
                                <Form.Control
                                  onChange={handleChange}
                                  value={photo_why || ""}
                                  as="textarea"
                                  type="text"
                                  onBlur={handleBlur}
                                  rows="2"
                                  style={{ minHeight: "calc(2 * 1.5em + 0.75rem + 2px)" }}
                                />
                              </Col>
                            </Form.Group>
                          </Col>
                          <Col></Col>
                        </Form.Group>
                        <Card>
                          <Card.Body>
                            <QueryList
                              isAuthenticated={isAuthenticated}
                              user={user}
                              data={queries}
                              isLoading={isLoading}
                              controlType="table"
                              updateTableState={updateTableState}
                              saveRecord={saveRecord}
                              deleteRecord={deleteRecord}
                              propertyId={params.id}
                              validateRelatedRecord={validateRelatedRecord}
                            />
                          </Card.Body>
                        </Card>
                      </Card.Body>
                    </Card>
                  </Tab>
                  <Tab
                    eventKey={11}
                    title={"Sale Sheets (" + activePropertySaleSheets.length + ")"}
                    tabAttrs={getPropertyTabAttrs(11)}
                  >
                    <Card>
                      {/* <Card.Header >Sale Sheets ({activePropertySaleSheets.length})</Card.Header> */}

                      <Card.Body>
                        <Card>
                          <Card.Body>
                            <PropertySaleSheetList
                              user={user}
                              isAuthenticated={isAuthenticated}
                              data={propertySaleSheets}
                              updateTableState={updateTableState}
                              saveRecord={(record) => saveRecord("propertySaleSheets", record)}
                              deleteRecord={deleteRecord}
                              validateRelatedRecord={validateRelatedRecord}
                              controlType="table"
                              propertyid={property_id}
                              propertykey={property_key}
                              idList={idList}
                            />
                          </Card.Body>
                        </Card>
                      </Card.Body>
                    </Card>
                  </Tab>

                  <Tab eventKey={13} title="Articles" tabAttrs={getPropertyTabAttrs(13)}>
                    <Card>
                      {/* <Card.Header > Articles ({propertyArticles.length})</Card.Header> */}

                      <Card.Body>
                        <Card>
                          <Card.Body>
                            <PropertyArticleList
                              isAuthenticated={isAuthenticated}
                              user={user}
                              data={propertyArticles}
                              updateTableState={updateTableState}
                              saveRecord={(record, dataSet, id) => saveRecord(dataSet, record)}
                              deleteRecord={deleteRecord}
                              validateRelatedRecord={validateRelatedRecord}
                              propertyid={property_id}
                              propertykey={property_key}
                              controlType="table"
                              articleCategories={articleCategories}
                              idList={idList}
                            />
                          </Card.Body>
                        </Card>
                      </Card.Body>
                    </Card>
                  </Tab>

                  <Tab eventKey={14} title="History" tabAttrs={getPropertyTabAttrs(14)}>
                    <Card>
                      {/* <Card.Header >Record History</Card.Header> */}

                      <Card.Body>
                        <Card style={{ width: "28rem" }}>
                          <Card.Body>
                            <Form.Group as={Row} controlId="created_date">
                              <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                Created Date
                              </Form.Label>
                              <Col>
                                <Form.Control
                                  value={
                                    created_date && Moment(created_date).isValid()
                                      ? Moment(created_date).local().format("DD-MM-YYYY")
                                      : ""
                                  }
                                  plaintext
                                  readOnly
                                />
                              </Col>
                            </Form.Group>

                            <Form.Group as={Row} controlId="created_by">
                              <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                Created By
                              </Form.Label>
                              <Col>
                                <Form.Control value={created_by ?? ""} plaintext readOnly />
                              </Col>
                            </Form.Group>

                            <Form.Group as={Row} controlId="last_edited_date">
                              <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                Last Edited Date
                              </Form.Label>
                              <Col>
                                <Form.Control
                                  value={
                                    last_edited_date && Moment(last_edited_date).isValid()
                                      ? Moment(last_edited_date).local().format("DD-MM-YYYY")
                                      : ""
                                  }
                                  plaintext
                                  readOnly
                                />
                              </Col>
                            </Form.Group>

                            <Form.Group as={Row} controlId="last_edited_by">
                              <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                Last Edited By
                              </Form.Label>
                              <Col>
                                <Form.Control value={last_edited_by ?? ""} plaintext readOnly />
                              </Col>
                            </Form.Group>
                            <Form.Group as={Row} controlId="archived_date">
                              <Form.Label className="text-right" column sm={6} md={6} lg={4}>
                                Archived Date
                              </Form.Label>
                              <Col>
                                <DatePicker
                                  selected={archived_date != null ? new Date(archived_date) : null}
                                  onChange={(e) => handleDateChange(e, "archived_date")}
                                  onBlur={handleBlur}
                                  dateFormat="dd/MM/yyyy"
                                  className="form-control"
                                  isClearable={true}
                                />
                              </Col>
                            </Form.Group>
                          </Card.Body>
                        </Card>
                      </Card.Body>
                    </Card>
                  </Tab>

                  {/* }*/}
                </Tabs>
              </Col>
            </Form.Group>
          </Form>
        </div>
      )}
    </div>
  );
}

export default PropertyDetail;
